// frontend/src/components/cadetes/CadetesList.tsx
// Vista principal de gestión de presentismo — Stitch Institutional Design

import { useState, useEffect, useMemo, useRef } from 'react';
import type { Cadete, CodigoNovedad, HistorialCadete, RegistroPresentismo } from '../../lib/api';
import { api, MSG_REQUIERE_BACKEND_NUEVO, esErrorDeRed, requiereBackendNuevo } from '../../lib/api';
import { storage } from '../../services/storage';
import {
  ESTADOS_QUE_ALTERAN_FUERZA, esEfectivoEnFecha, estadoInstitucionalEnFecha, formatHoraLocal,
  getArgentinaDateString, getArgentinaNow, toLocalDateString,
} from '../../lib/utils';
import {
  CODIGOS_NOVEDAD, NOMBRE_CODIGO, codigoDeRegistro, diasEntre, estiloCodigo, estiloEstado,
  fechaCorta, licenciaSolapa, sumarDias, tiposLicenciaActivosOrdenados, validarRango,
} from '../../lib/novedades';
import { CadeteCard } from './CadeteCard';
import { useOfflineSync } from '../../hooks/useOfflineSync';
import {
  Activity, AlertTriangle, ArrowDownAZ, ArrowRightLeft, ArrowUpDown, Award,
  BadgeCheck, BellOff, BellRing, Building2, CalendarDays, CheckCircle2,
  CheckCheck, ChevronLeft, ChevronRight, ClipboardCheck, Clock, FilePlus, Hourglass,
  Info, LogOut, Medal, PlusCircle, RefreshCw, Save, Scale, Search, SearchX,
  Shield, Stethoscope, Trash2, Undo2, User, UserCheck, UserX, Users, WifiOff, X, XCircle,
  type LucideIcon,
} from 'lucide-react';
import { toast } from '../../lib/toast';
import { AnimatePresence, m } from 'motion/react';
import { MODAL_OVERLAY, MODAL_TARJETA } from '../../lib/motion';
import { AnimatedNumber } from '../ui/AnimatedNumber';
import { MarcarPresentesSheet } from './MarcarPresentesSheet';
import { esperarMarcasEnVuelo, iniciarMarcaEnVuelo, solicitarFlushMarcas } from '../../lib/marcasEnVuelo';

interface CadetesListProps {
  fecha: string;
  activeTab: 'dashboard' | 'presentismo' | 'gestion' | 'reportes';
  compania?: string;
  onRegistroChange?: () => void;
  onCompaniaChange?: (cia: string) => void;
}

type FiltroEstado = 'todos' | 'presentes' | 'ausentes' | 'pendientes' | 'convocada';

// Historial modal (inline)
function LocalInstitutionalBadge({ estado }: { estado?: string }) {
  const base = "flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] uppercase tracking-tight font-semibold border w-fit";
  const current = (estado === 'Aspirante a Cadete' || !estado) ? 'Cadete' : estado;
  
  type BadgeStyle = { bg: string; color: string; border: string; Icon: LucideIcon; label: string };

  let styles: BadgeStyle = {
    bg: '#eae8e7', color: '#43474f', border: '#c3c6d1', Icon: UserCheck, label: 'Aspirante a Cadete',
  };

  if (current === 'Cadete') {
    styles = { bg: '#d5e3ff', color: '#001b3c', border: '#799dd6', Icon: Shield, label: 'Cursante' };
  } else if (current === 'Oficial Recibido') {
    styles = { bg: '#d1fae5', color: '#065f46', border: '#10b981', Icon: Award, label: 'Oficial Recibido' };
  } else if (current === 'Excluido') {
    styles = { bg: '#ffdad6', color: '#93000a', border: '#ba1a1a', Icon: Scale, label: 'Excluido' };
  } else if (current === 'Desestimiento') {
    styles = { bg: '#ffedd5', color: '#9a3412', border: '#f97316', Icon: LogOut, label: 'Desestimiento' };
  } else if (current === 'Ausente a Convocatoria') {
    styles = { bg: '#f3f4f6', color: '#1f2937', border: '#9ca3af', Icon: UserX, label: 'Ausente Convocatoria' };
  } else if (current === 'Traspaso') {
    styles = { bg: '#f3e8ff', color: '#6b21a8', border: '#a855f7', Icon: ArrowRightLeft, label: 'Traspaso' };
  }

  const BadgeIcon = styles.Icon;
  return (
    <span
      className={base}
      style={{ backgroundColor: styles.bg, color: styles.color, borderColor: styles.border }}
    >
      <BadgeIcon size={12} strokeWidth={2} />
      {styles.label}
    </span>
  );
}

function HistorialModal({
  open,
  cadete,
  registros,
  loading,
  onClose,
  sincronizar,
}: {
  open: boolean;
  cadete: Cadete | null;
  // Registros del cadete guardados en IndexedDB (últimos 30 días): respaldo sin conexión
  registros: RegistroPresentismo[];
  loading: boolean;
  onClose: () => void;
  sincronizar: () => Promise<void>;
}) {
  const [activeTab, setActiveTab] = useState<'datos' | 'historial' | 'licencias'>('licencias');
  const [historialPeriod, setHistorialPeriod] = useState<'semana' | 'mes' | 'rango'>('semana');
  const hoy = getArgentinaDateString();
  const [rangoDesde, setRangoDesde] = useState(() => sumarDias(getArgentinaDateString(), -89));
  const [rangoHasta, setRangoHasta] = useState(() => getArgentinaDateString());

  // Historial del servidor (backend nuevo) y aviso de degradación
  const [historialRemoto, setHistorialRemoto] = useState<HistorialCadete | null>(null);
  const [cargandoRemoto, setCargandoRemoto] = useState(false);
  const [avisoHistorial, setAvisoHistorial] = useState<{ tipo: 'offline' | 'backend' | 'error'; texto: string } | null>(null);
  const historialReqRef = useRef(0);

  // Licencias states
  const [licenciasCadete, setLicenciasCadete] = useState<any[]>([]);
  const [tiposLicencias, setTiposLicencias] = useState<any[]>([]);
  const [loadingLicencias, setLoadingLicencias] = useState(false);
  const [mostrarFormLicencia, setMostrarFormLicencia] = useState(false);

  // Form states
  const [formLicenciaTipo, setFormLicenciaTipo] = useState<number>(0);
  const [formLicenciaDesde, setFormLicenciaDesde] = useState('');
  const [formLicenciaHasta, setFormLicenciaHasta] = useState('');
  const [formLicenciaMotivo, setFormLicenciaMotivo] = useState('');
  const [formLicenciaCargando, setFormLicenciaCargando] = useState(false);

  const loadLicencias = async () => {
    if (!cadete) return;
    try {
      setLoadingLicencias(true);
      // El catálogo local puede traer tipos inactivos (LPA, LMA, LFF, LEX…):
      // para elegir solo se ofrecen los activos, RMH/ART/LE/PE primero.
      const allTypes = tiposLicenciaActivosOrdenados(await storage.getTiposLicencias());
      setTiposLicencias(allTypes);
      if (allTypes.length > 0 && (formLicenciaTipo === 0 || !allTypes.some((t) => t.id === formLicenciaTipo))) {
        setFormLicenciaTipo(allTypes[0].id);
      }

      const allLicencias = await storage.getLicencias();
      const filtered = allLicencias
        .filter(l => l.cadete_id === cadete.id)
        .sort((a, b) => String(b.fecha_inicio || '').localeCompare(String(a.fecha_inicio || '')));
      setLicenciasCadete(filtered);
    } catch (error) {
      console.error('Error al cargar licencias locales:', error);
    } finally {
      setLoadingLicencias(false);
    }
  };

  useEffect(() => {
    if (open) {
      // Abre en Licencias; orden de pestañas: Licencias, Ficha, Asistencia
      setActiveTab('licencias');
      setHistorialPeriod('semana');
      setMostrarFormLicencia(false);
      setHistorialRemoto(null);
      setAvisoHistorial(null);
      loadLicencias();
    }
  }, [open, cadete]);

  useEffect(() => {
    if (activeTab === 'licencias') {
      loadLicencias();
    }
  }, [activeTab]);

  // Date calculation utilities (local Buenos Aires time -03:00)
  const getPoliceWeekRange = () => {
    const now = getArgentinaNow();
    const day = now.getDay(); // 0: Sunday, 1: Monday... 6: Saturday

    // Sunday at 20:00
    const sunday = new Date(now);
    sunday.setDate(now.getDate() - day);
    sunday.setHours(20, 0, 0, 0);

    // Friday at 20:00
    const friday = new Date(now);
    friday.setDate(now.getDate() - day + 5);
    friday.setHours(20, 0, 0, 0);

    // Adjust if weekend (Saturday or Sunday before 20:00) to show the completed week
    if (day === 6 || (day === 0 && now.getHours() < 20)) {
      sunday.setDate(sunday.getDate() - 7);
      friday.setDate(friday.getDate() - 7);
    }

    return { start: sunday, end: friday };
  };

  const getMonthRange = () => {
    const now = getArgentinaNow();
    const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  };

  const parseRecordDate = (fecha: string, horaLocal?: string): Date => {
    const [year, month, day] = fecha.split('-').map(Number);
    const [hours, minutes, seconds] = (horaLocal || '00:00:00').split(':').map(Number);
    return new Date(year, month - 1, day, hours || 0, minutes || 0, seconds || 0);
  };

  const formatShortDate = (d: Date) => {
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${day}/${month}`;
  };

  // Rango (YYYY-MM-DD) del período seleccionado, para pedirlo al servidor y
  // filtrar licencias. La semana policial además se acota por hora (20:00).
  const errorRango = historialPeriod === 'rango' ? validarRango(rangoDesde, rangoHasta) : null;
  const rangoPeriodo = (() => {
    if (historialPeriod === 'semana') {
      const { start, end } = getPoliceWeekRange();
      return { desde: toLocalDateString(start), hasta: toLocalDateString(end) };
    }
    if (historialPeriod === 'mes') {
      const { start, end } = getMonthRange();
      return { desde: toLocalDateString(start), hasta: toLocalDateString(end) };
    }
    return errorRango ? null : { desde: rangoDesde, hasta: rangoHasta };
  })();

  // Con conexión: historial completo del servidor (todas las opciones de período).
  // Sin conexión / backend viejo / error: se cae a IndexedDB con un aviso.
  useEffect(() => {
    if (!open || !cadete || activeTab !== 'historial' || !rangoPeriodo) return;
    const reqId = ++historialReqRef.current;
    const { desde, hasta } = rangoPeriodo;

    if (!navigator.onLine) {
      setHistorialRemoto(null);
      setAvisoHistorial({ tipo: 'offline', texto: 'Sin conexión: se muestran solo los últimos 30 días guardados en el dispositivo.' });
      return;
    }

    setCargandoRemoto(true);
    api.getHistorialCadete(cadete.id, desde, hasta)
      .then((res) => {
        if (reqId !== historialReqRef.current) return;
        if (res.success && res.data) {
          setHistorialRemoto(res.data);
          setAvisoHistorial(null);
        } else {
          setHistorialRemoto(null);
          setAvisoHistorial({ tipo: 'error', texto: res.error || 'No se pudo obtener el historial del servidor; se muestran los datos del dispositivo.' });
        }
      })
      .catch((err) => {
        if (reqId !== historialReqRef.current) return;
        setHistorialRemoto(null);
        if (requiereBackendNuevo(err)) {
          setAvisoHistorial({ tipo: 'backend', texto: `${MSG_REQUIERE_BACKEND_NUEVO} Se muestran solo los últimos 30 días guardados en el dispositivo.` });
        } else if (esErrorDeRed(err)) {
          setAvisoHistorial({ tipo: 'offline', texto: 'Sin conexión: se muestran solo los últimos 30 días guardados en el dispositivo.' });
        } else {
          console.error('Error al cargar historial del servidor:', err);
          setAvisoHistorial({ tipo: 'error', texto: 'No se pudo obtener el historial del servidor; se muestran los datos del dispositivo.' });
        }
      })
      .finally(() => {
        if (reqId === historialReqRef.current) setCargandoRemoto(false);
      });
  }, [open, cadete?.id, activeTab, rangoPeriodo?.desde, rangoPeriodo?.hasta]);

  if (!open || !cadete) return null;

  const labelEstado = (e: string) =>
    e === 'presente' ? 'Presente' : e === 'ausente' ? 'Ausente' : e === 'tarde' ? 'Tarde' : e;

  const statusColor = (e: string) => estiloEstado(e);

  // Registros a mostrar: del servidor si llegaron (y coinciden con el rango pedido),
  // si no los de IndexedDB. En "semana" se acotan por hora (dom 20:00 → vie 20:00).
  const usaRemoto = !!historialRemoto && !!rangoPeriodo
    && historialRemoto.cadete?.id === cadete.id
    && historialRemoto.desde === rangoPeriodo.desde && historialRemoto.hasta === rangoPeriodo.hasta;
  const fuente: RegistroPresentismo[] = usaRemoto ? historialRemoto!.registros : registros;
  const filteredRegistros = fuente
    .filter((r) => {
      if (!rangoPeriodo) return false;
      if (historialPeriod === 'semana') {
        const rd = parseRecordDate(r.fecha, r.hora_local);
        const { start, end } = getPoliceWeekRange();
        return rd >= start && rd <= end;
      }
      return r.fecha >= rangoPeriodo.desde && r.fecha <= rangoPeriodo.hasta;
    })
    .slice()
    .sort((a, b) => `${b.fecha}T${b.hora_local || '00:00'}`.localeCompare(`${a.fecha}T${a.hora_local || '00:00'}`));

  const licenciasPeriodo: any[] = (usaRemoto ? historialRemoto!.licencias : licenciasCadete)
    .filter((l) => rangoPeriodo && licenciaSolapa(l, rangoPeriodo.desde, rangoPeriodo.hasta))
    .slice()
    .sort((a, b) => String(b.fecha_inicio || '').localeCompare(String(a.fecha_inicio || '')));

  // Resumen del período calculado sobre lo que se muestra (coincide con el del
  // servidor cuando se muestra el rango completo).
  const resumen = (() => {
    const porCodigo = Object.fromEntries(CODIGOS_NOVEDAD.map((c) => [c, 0])) as Record<CodigoNovedad, number>;
    let presentes = 0, tardes = 0, ausentes = 0;
    for (const r of filteredRegistros) {
      if (r.estado === 'presente') presentes++;
      else if (r.estado === 'tarde') tardes++;
      else if (r.estado === 'ausente') ausentes++;
      const cod = codigoDeRegistro(r);
      if (cod) porCodigo[cod]++;
    }
    return { presentes, tardes, ausentes, porCodigo, licencias: licenciasPeriodo.length };
  })();

  const cargandoHistorial = loading || cargandoRemoto;

  const handleCreateLicencia = async () => {
    if (!formLicenciaTipo || !formLicenciaDesde) {
      toast.error('Por favor complete todos los campos requeridos (Tipo, Desde).');
      return;
    }

    if (formLicenciaHasta && new Date(formLicenciaDesde) > new Date(formLicenciaHasta)) {
      toast.error('La fecha de inicio no puede ser posterior a la fecha de fin.');
      return;
    }

    try {
      setFormLicenciaCargando(true);

      const payload = {
        cadete_id: cadete.id,
        tipo_licencia_id: formLicenciaTipo,
        fecha_inicio: formLicenciaDesde,
        fecha_fin: formLicenciaHasta || null,
        motivo_detalle: formLicenciaMotivo.trim() || undefined,
      };

      const selectedTipo = tiposLicencias.find(t => t.id === formLicenciaTipo);

      const tempId = await storage.saveLicenciaLocal({
        ...payload,
        tipo_nombre: selectedTipo?.nombre || 'Licencia',
        tipo_codigo: selectedTipo?.codigo || 'LIC',
      });

      await storage.enqueueOp({ type: 'createLicencia', payload: { ...payload, _tempId: tempId } });

      setFormLicenciaTipo(tiposLicencias[0]?.id ?? 0);
      setFormLicenciaDesde('');
      setFormLicenciaHasta('');
      setFormLicenciaMotivo('');
      setMostrarFormLicencia(false);
      await loadLicencias();
      window.dispatchEvent(new CustomEvent('sgcc:sync-data-changed'));
      if (navigator.onLine) {
        sincronizar().catch((err) => console.error('Error en sincronización automática tras handleCreateLicencia:', err));
      }
    } catch (e: any) {
      console.error('Error al registrar licencia:', e);
      toast.error(e.message || 'Error al guardar la licencia.');
    } finally {
      setFormLicenciaCargando(false);
    }
  };

  const handleDeleteLicencia = async (licenciaId: string) => {
    if (!confirm('¿Está seguro de que desea anular esta licencia?')) return;

    try {
      setLoadingLicencias(true);

      const allLocal = await storage.getLicencias();
      const target = allLocal.find(l => l.id === licenciaId);
      // Local delete happens before the queue write; if enqueueOp throws the
      // licencia is already removed locally. Acceptable trade-off: the sync
      // will not delete it on the server, but the local state is consistent.
      await storage.setLicencias(allLocal.filter(l => l.id !== licenciaId));

      if (licenciaId.startsWith('temp-')) {
        // Cancel the pending createLicencia op for this temp licencia
        const queue = await storage.getQueue();
        const matchingOp = queue.find(
          op =>
            op.type === 'createLicencia' &&
            op.payload.cadete_id === cadete.id &&
            op.payload.fecha_inicio === target?.fecha_inicio,
        );
        if (matchingOp) {
          await storage.deleteQueueItem(matchingOp.id);
        }
      } else {
        // Queue a delete op to be synced with the server later
        await storage.enqueueOp({ type: 'deleteLicencia', payload: { id: licenciaId } });
      }

      await loadLicencias();
      window.dispatchEvent(new CustomEvent('sgcc:sync-data-changed'));
      if (navigator.onLine) {
        sincronizar().catch((err) => console.error('Error en sincronización automática tras handleDeleteLicencia:', err));
      }
    } catch (e: any) {
      console.error('Error al eliminar licencia:', e);
      toast.error(e.message || 'Error al eliminar la licencia.');
    } finally {
      setLoadingLicencias(false);
    }
  };

  const ChipCodigo = ({ codigo, cantidad }: { codigo: string; cantidad?: number }) => {
    const sc = estiloCodigo(codigo);
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-extrabold uppercase tracking-wider border whitespace-nowrap"
        style={{ backgroundColor: sc.bg, color: sc.color, borderColor: sc.border, fontFamily: 'Inter, sans-serif', opacity: cantidad === 0 ? 0.45 : 1 }}
        title={NOMBRE_CODIGO[codigo as CodigoNovedad] || codigo}
      >
        {codigo}
        {cantidad !== undefined && <span className="tabular-nums">{cantidad}</span>}
      </span>
    );
  };

  const periodButtonClass = (p: 'semana' | 'mes' | 'rango') =>
    `flex-1 py-1.5 text-[11px] font-bold rounded-full transition-all uppercase tracking-wider ${
      historialPeriod === p ? 'bg-white shadow-sm text-[#001e40]' : 'text-[#43474f] hover:bg-[#eae8e7]'
    }`;

  return (
    <m.div {...MODAL_OVERLAY} className="fixed inset-0 z-[9000] flex items-center justify-center p-4 bg-[#001e40]/40 backdrop-blur-sm" onClick={onClose}>
      <m.div
        {...MODAL_TARJETA}
        className="relative w-full max-w-md shadow-2xl rounded-xl overflow-hidden border border-[#c3c6d1] flex flex-col max-h-[85vh] bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-[#001e40] p-5 flex justify-between items-start flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-white">
              <User size={20} strokeWidth={1.8} />
            </div>
            <div>
              <h3 className="font-bold text-base text-white" style={{ fontFamily: 'Inter, sans-serif' }}>Ficha de Personal</h3>
              <p className="text-xs text-white/70 uppercase tracking-widest mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                {cadete.apellido}, {cadete.nombres}
              </p>
            </div>
          </div>
          <button className="text-white/70 hover:text-white hover:bg-white/10 p-1 rounded-full transition-colors tap-active" onClick={onClose}>
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        {/* Tab Controls */}
        <div className="flex border-b border-[#c3c6d1] bg-[#f6f3f2] flex-shrink-0">
          <button
            className={`flex-1 py-3 text-center text-[11px] sm:text-xs font-bold uppercase tracking-wider relative transition-colors ${
              activeTab === 'licencias' ? 'text-[#735c00]' : 'text-[#43474f] hover:text-[#735c00]'
            }`}
            onClick={() => setActiveTab('licencias')}
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Licencias
            {activeTab === 'licencias' && (
              <span className="absolute bottom-0 left-0 w-full h-[3px] bg-[#735c00]" />
            )}
          </button>
          <button
            className={`flex-1 py-3 text-center text-[11px] sm:text-xs font-bold uppercase tracking-wider relative transition-colors ${
              activeTab === 'datos' ? 'text-[#735c00]' : 'text-[#43474f] hover:text-[#735c00]'
            }`}
            onClick={() => setActiveTab('datos')}
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Ficha
            {activeTab === 'datos' && (
              <span className="absolute bottom-0 left-0 w-full h-[3px] bg-[#735c00]" />
            )}
          </button>
          <button
            className={`flex-1 py-3 text-center text-[11px] sm:text-xs font-bold uppercase tracking-wider relative transition-colors ${
              activeTab === 'historial' ? 'text-[#735c00]' : 'text-[#43474f] hover:text-[#735c00]'
            }`}
            onClick={() => setActiveTab('historial')}
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Asistencia
            {activeTab === 'historial' && (
              <span className="absolute bottom-0 left-0 w-full h-[3px] bg-[#735c00]" />
            )}
          </button>
        </div>

        {/* Tab Content 1: Datos */}
        {activeTab === 'datos' && (
          <div className="overflow-y-auto flex-1 p-6 space-y-4 bg-white">
            <div className="p-4 rounded-xl border border-[#c3c6d1] bg-[#fbf9f8] space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-[#c3c6d1]/40">
                <span className="text-xs font-semibold text-[#737780] uppercase tracking-wider" style={{ fontFamily: 'Inter, sans-serif' }}>DNI</span>
                <span className="text-sm font-bold text-[#001e40]" style={{ fontFamily: 'Inter, sans-serif' }}>{cadete.dni}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-[#c3c6d1]/40">
                <span className="text-xs font-semibold text-[#737780] uppercase tracking-wider" style={{ fontFamily: 'Inter, sans-serif' }}>Localidad</span>
                <span className="text-sm text-[#001e40]" style={{ fontFamily: 'Inter, sans-serif' }}>{cadete.localidad || 'No registrada'}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-[#c3c6d1]/40">
                <span className="text-xs font-semibold text-[#737780] uppercase tracking-wider" style={{ fontFamily: 'Inter, sans-serif' }}>Partido</span>
                <span className="text-sm text-[#001e40]" style={{ fontFamily: 'Inter, sans-serif' }}>{cadete.partido || 'No registrado'}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-[#c3c6d1]/40">
                <span className="text-xs font-semibold text-[#737780] uppercase tracking-wider" style={{ fontFamily: 'Inter, sans-serif' }}>Teléfono Principal</span>
                <span className="text-sm text-[#001e40]" style={{ fontFamily: 'Inter, sans-serif' }}>{cadete.telefono_principal || 'No registrado'}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-[#c3c6d1]/40">
                <span className="text-xs font-semibold text-[#737780] uppercase tracking-wider" style={{ fontFamily: 'Inter, sans-serif' }}>Teléfono Alternativo</span>
                <span className="text-sm text-[#001e40]" style={{ fontFamily: 'Inter, sans-serif' }}>{cadete.telefono_alternativo || 'No registrado'}</span>
              </div>
              <div className="flex justify-between items-start pb-1">
                <span className="text-xs font-semibold text-[#737780] uppercase tracking-wider pt-1" style={{ fontFamily: 'Inter, sans-serif' }}>Situación Académica</span>
                <LocalInstitutionalBadge estado={cadete.estado_institucional} />
              </div>

              {/* Show metadata if set */}
              {cadete.estado_institucional && cadete.estado_institucional !== 'Aspirante a Cadete' && cadete.estado_metadata && (
                <div className="mt-4 p-3 rounded-lg bg-[#eae8e7] text-xs space-y-1 text-[#43474f] border border-[#c3c6d1]">
                  <p className="font-semibold uppercase tracking-wider text-[10px] text-[#737780]" style={{ fontFamily: 'Inter, sans-serif' }}>Detalles del estado actual:</p>
                  {cadete.estado_metadata.fecha && (
                    <p style={{ fontFamily: 'Inter, sans-serif' }}><strong>Fecha de registro:</strong> {cadete.estado_metadata.fecha}</p>
                  )}
                  {cadete.estado_metadata.motivo && (
                    <p style={{ fontFamily: 'Inter, sans-serif' }}><strong>Motivo:</strong> {cadete.estado_metadata.motivo}</p>
                  )}
                  {cadete.estado_metadata.descripcion && (
                    <p style={{ fontFamily: 'Inter, sans-serif' }}><strong>Descripción:</strong> {cadete.estado_metadata.descripcion}</p>
                  )}
                  {cadete.estado_metadata.compania_destino && (
                    <p style={{ fontFamily: 'Inter, sans-serif' }}><strong>Compañía destino:</strong> {cadete.estado_metadata.compania_destino}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab Content 2: Historial */}
        {activeTab === 'historial' && (
          <div className="overflow-y-auto flex-1 p-5 space-y-3 bg-white max-h-[60vh]">
            {/* Segmented control: Semana / Mes / Período */}
            <div className="flex gap-1 bg-[#f0eded] p-1 rounded-full w-full flex-shrink-0">
              <button onClick={() => setHistorialPeriod('semana')} className={periodButtonClass('semana')} style={{ fontFamily: 'Inter, sans-serif' }}>
                Semana
              </button>
              <button onClick={() => setHistorialPeriod('mes')} className={periodButtonClass('mes')} style={{ fontFamily: 'Inter, sans-serif' }}>
                Mes
              </button>
              <button onClick={() => setHistorialPeriod('rango')} className={periodButtonClass('rango')} style={{ fontFamily: 'Inter, sans-serif' }}>
                Período
              </button>
            </div>

            {/* Período libre: dos fechas, máx. 366 días */}
            {historialPeriod === 'rango' && (
              <div className="grid grid-cols-2 gap-2 animate-fade-in">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-[#737780] uppercase tracking-wider" htmlFor="hist-desde">Desde</label>
                  <input
                    id="hist-desde"
                    type="date"
                    value={rangoDesde}
                    max={hoy}
                    onChange={(e) => setRangoDesde(e.target.value)}
                    className="w-full h-9 px-2 border border-[#c3c6d1] rounded-lg text-xs font-bold text-[#001e40] bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-[#737780] uppercase tracking-wider" htmlFor="hist-hasta">Hasta</label>
                  <input
                    id="hist-hasta"
                    type="date"
                    value={rangoHasta}
                    onChange={(e) => setRangoHasta(e.target.value)}
                    className="w-full h-9 px-2 border border-[#c3c6d1] rounded-lg text-xs font-bold text-[#001e40] bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                </div>
                {errorRango && (
                  <p className="col-span-2 text-[11px] font-medium" style={{ color: '#ba1a1a', fontFamily: 'Inter, sans-serif' }}>{errorRango}</p>
                )}
              </div>
            )}

            {/* Subtitle with date range */}
            <div className="text-[10px] font-bold text-[#43474f] uppercase tracking-wider text-center flex-shrink-0 bg-[#f6f3f2] py-2 px-1 rounded-lg border border-[#c3c6d1]/40" style={{ fontFamily: 'Inter, sans-serif' }}>
              {historialPeriod === 'semana' ? (
                <span>
                  Domingo {formatShortDate(getPoliceWeekRange().start)} 20:00 hs al Viernes {formatShortDate(getPoliceWeekRange().end)} 20:00 hs
                </span>
              ) : historialPeriod === 'mes' ? (
                <span>
                  Mes: 01/{((getArgentinaNow().getMonth() + 1).toString().padStart(2, '0'))} al {formatShortDate(getMonthRange().end)}/{((getArgentinaNow().getMonth() + 1).toString().padStart(2, '0'))}
                </span>
              ) : rangoPeriodo ? (
                <span>Del {fechaCorta(rangoPeriodo.desde)} al {fechaCorta(rangoPeriodo.hasta)} · {diasEntre(rangoPeriodo.desde, rangoPeriodo.hasta) + 1} días</span>
              ) : (
                <span>Período inválido</span>
              )}
            </div>

            {/* Aviso de degradación (offline / backend viejo / error) */}
            {avisoHistorial && (
              <div
                className="flex items-start gap-2 p-2.5 rounded-lg border text-[11px] leading-snug animate-fade-in"
                style={avisoHistorial.tipo === 'offline'
                  ? { backgroundColor: '#fff8e1', borderColor: 'rgba(115,92,0,0.3)', color: '#745c00' }
                  : { backgroundColor: '#ffdad6', borderColor: 'rgba(186,26,26,0.25)', color: '#93000a' }}
              >
                {avisoHistorial.tipo === 'offline'
                  ? <WifiOff size={14} strokeWidth={2} className="shrink-0 mt-0.5" />
                  : <AlertTriangle size={14} strokeWidth={2} className="shrink-0 mt-0.5" />}
                <span style={{ fontFamily: 'Inter, sans-serif' }}>{avisoHistorial.texto}</span>
              </div>
            )}

            {/* Resumen del período */}
            {!cargandoHistorial && rangoPeriodo && (
              <div className="p-3 rounded-xl border border-[#c3c6d1] bg-[#fbf9f8] space-y-2.5">
                <div className="grid grid-cols-3 gap-2 text-center">
                  {([
                    { label: 'Presentes', value: resumen.presentes, estilo: estiloEstado('presente') },
                    { label: 'Tardes', value: resumen.tardes, estilo: estiloEstado('tarde') },
                    { label: 'Ausentes', value: resumen.ausentes, estilo: estiloEstado('ausente') },
                  ] as const).map((m) => (
                    <div key={m.label} className="rounded-lg border py-1.5" style={{ backgroundColor: m.estilo.bg, borderColor: m.estilo.border }}>
                      <p className="text-lg font-black tabular-nums leading-none" style={{ color: m.estilo.color, fontFamily: 'Inter, sans-serif' }}>{m.value}</p>
                      <p className="text-[9px] font-bold uppercase tracking-wider mt-1" style={{ color: m.estilo.color, fontFamily: 'Inter, sans-serif' }}>{m.label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {CODIGOS_NOVEDAD.map((c) => (
                    <ChipCodigo key={c} codigo={c} cantidad={resumen.porCodigo[c]} />
                  ))}
                  <span className="ml-auto text-[10px] font-bold text-[#43474f] uppercase tracking-wider" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {resumen.licencias} {resumen.licencias === 1 ? 'licencia' : 'licencias'}
                  </span>
                </div>
              </div>
            )}

            {/* List of records */}
            <div className="space-y-2">
              {cargandoHistorial && (
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-[#737780]">
                  <RefreshCw size={36} strokeWidth={1.6} className="animate-spin-slow" />
                  <span className="text-sm font-semibold">Cargando historial...</span>
                </div>
              )}
              {!cargandoHistorial && filteredRegistros.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-[#737780] text-center border-2 border-dashed border-[#c3c6d1] rounded-xl bg-[#fbf9f8] px-4">
                  <Info size={24} strokeWidth={1.6} style={{ color: '#c3c6d1' }} className="mb-1" />
                  <p className="text-[10px] font-bold uppercase tracking-wider">Sin marcas registradas</p>
                  <p className="text-[9px] mt-0.5 leading-tight" style={{ color: '#737780' }}>
                    {historialPeriod === 'semana'
                      ? 'No se encontraron asistencias en este rango semanal.'
                      : historialPeriod === 'mes'
                        ? 'No se encontraron asistencias en este mes.'
                        : 'No se encontraron asistencias en el período seleccionado.'}
                  </p>
                </div>
              )}
              {!cargandoHistorial && filteredRegistros.map((r) => {
                const sc = statusColor(r.estado);
                const codigo = codigoDeRegistro(r);
                return (
                  <div
                    key={r.local_id || r.id || `${r.cadete_id}-${r.fecha}`}
                    className="flex items-start justify-between gap-2 p-3 border border-[#c3c6d1] rounded-lg bg-[#ffffff] hover:bg-[#f6f3f2] transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold text-[#001e40]" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {fechaCorta(r.fecha)} · {r.hora_local ? String(r.hora_local).substring(0, 5) : 'Sin hora'}
                      </p>
                      {r.motivo && (
                        <p className="text-[10px] italic mt-0.5 text-[#737780] leading-snug break-words">
                          {r.motivo}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span
                        className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border"
                        style={{ backgroundColor: sc.bg, color: sc.color, borderColor: sc.border, fontFamily: 'Inter, sans-serif' }}
                      >
                        {labelEstado(r.estado)}
                      </span>
                      {codigo && <ChipCodigo codigo={codigo} />}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Licencias en el período */}
            {!cargandoHistorial && rangoPeriodo && (
              <div className="pt-1 space-y-2">
                <h4 className="text-[10px] font-bold text-[#737780] uppercase tracking-wider flex items-center gap-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <Award size={12} strokeWidth={2} />
                  Licencias en el período ({licenciasPeriodo.length})
                </h4>
                {licenciasPeriodo.length === 0 ? (
                  <p className="text-[10px] text-[#737780] italic px-1">Sin licencias que se solapen con el período.</p>
                ) : (
                  licenciasPeriodo.map((lic) => (
                    <div key={lic.id} className="p-3 border border-[#c3c6d1] rounded-lg bg-white flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <ChipCodigo codigo={lic.tipo_codigo || 'LIC'} />
                          <span className="text-xs font-bold text-[#001e40] truncate" style={{ fontFamily: 'Inter, sans-serif' }}>
                            {lic.tipo_nombre || 'Licencia'}
                          </span>
                        </div>
                        <p className="text-[10px] font-semibold text-[#43474f] mt-1.5 flex items-center gap-1">
                          <CalendarDays size={12} strokeWidth={2} />
                          Del {fechaCorta(lic.fecha_inicio)} {lic.fecha_fin ? `al ${fechaCorta(lic.fecha_fin)}` : '(hasta alta)'}
                        </p>
                        {lic.motivo_detalle && (
                          <p className="text-[10px] italic text-[#737780] mt-1 leading-snug border-l-2 border-[#c3c6d1] pl-2 bg-[#fbf9f8] py-0.5 break-words">
                            {lic.motivo_detalle}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Tab Content 3: Licencias */}
        {activeTab === 'licencias' && (
          <div className="overflow-y-auto flex-1 p-6 space-y-4 bg-white flex flex-col max-h-[55vh]">
            {!mostrarFormLicencia ? (
              <>
                <button
                  onClick={() => setMostrarFormLicencia(true)}
                  className="w-full h-11 bg-[#001e40] hover:bg-[#003366] text-white rounded-lg font-semibold text-xs flex items-center justify-center gap-2 shadow-sm transition-all active:scale-98 flex-shrink-0"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <PlusCircle size={16} strokeWidth={2} />
                  Cargar Nueva Licencia
                </button>

                <div className="space-y-3 flex-1 overflow-y-auto min-h-0 pt-2">
                  {loadingLicencias ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-2 text-[#737780]">
                      <RefreshCw size={28} strokeWidth={1.6} className="animate-spin-slow" />
                      <span className="text-xs font-semibold">Cargando licencias...</span>
                    </div>
                  ) : licenciasCadete.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-[#737780] text-center border border-dashed border-[#c3c6d1] rounded-xl bg-[#fbf9f8] px-4">
                      <Award size={24} strokeWidth={1.6} className="text-[#c3c6d1] mb-1" />
                      <p className="text-[10px] font-bold uppercase tracking-wider">Sin Licencias Registradas</p>
                      <p className="text-[9px] mt-0.5" style={{ color: '#737780' }}>
                        Este cadete no registra licencias activas ni históricas.
                      </p>
                    </div>
                  ) : (
                    licenciasCadete.map((lic) => (
                      <div
                        key={lic.id}
                        className="p-3 border border-[#c3c6d1] rounded-lg bg-white shadow-sm flex items-start justify-between gap-3 hover:border-secondary/30 transition-all"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <ChipCodigo codigo={lic.tipo_codigo || 'LIC'} />
                            <span className="text-xs font-bold text-[#001e40] truncate" style={{ fontFamily: 'Inter, sans-serif' }}>
                              {lic.tipo_nombre}
                            </span>
                          </div>
                          <p className="text-[10px] font-semibold text-[#43474f] mt-1.5 flex items-center gap-1">
                            <CalendarDays size={12} strokeWidth={2} />
                            Del {fechaCorta(lic.fecha_inicio)} {lic.fecha_fin ? `al ${fechaCorta(lic.fecha_fin)}` : "(hasta alta)"}
                          </p>
                          {lic.motivo_detalle && (
                            <p className="text-[10px] italic text-[#737780] mt-1 leading-normal border-l-2 border-[#c3c6d1] pl-2 bg-[#fbf9f8] py-0.5 break-words">
                              {lic.motivo_detalle}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteLicencia(lic.id)}
                          className="text-[#737780] hover:text-red-600 hover:bg-red-50 p-1.5 rounded-full transition-colors active:scale-95"
                          title="Anular licencia"
                        >
                          <Trash2 size={18} strokeWidth={2} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : (
              /* Formulario de Carga */
              <div className="space-y-4 pt-2">
                <div className="flex justify-between items-center pb-2 border-b border-[#c3c6d1]/40 flex-shrink-0">
                  <h4 className="font-bold text-xs text-[#001e40] uppercase tracking-wider flex items-center gap-1.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                    <FilePlus size={16} strokeWidth={2} />
                    Nueva Licencia de Cursada
                  </h4>
                  <button
                    onClick={() => setMostrarFormLicencia(false)}
                    className="text-[#737780] hover:text-[#1b1c1c] text-xs font-semibold flex items-center gap-0.5"
                  >
                    <ChevronLeft size={16} strokeWidth={2} />
                    Volver
                  </button>
                </div>

                <div className="space-y-3.5">
                  {/* Tipo */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-[#737780] uppercase tracking-wider" htmlFor="input-tipo-lic">
                      Tipo de Licencia *
                    </label>
                    <select
                      id="input-tipo-lic"
                      className="w-full p-2.5 border border-[#c3c6d1] rounded-lg text-xs bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all"
                      value={formLicenciaTipo}
                      onChange={(e) => setFormLicenciaTipo(parseInt(e.target.value))}
                    >
                      {tiposLicencias.length === 0 && (
                        <option value={0}>Sin tipos de licencia activos (sincronice)</option>
                      )}
                      {tiposLicencias.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.codigo} — {t.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Fechas */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-[#737780] uppercase tracking-wider" htmlFor="input-lic-desde">
                        Fecha Inicio *
                      </label>
                      <input
                        id="input-lic-desde"
                        type="date"
                        className="w-full p-2 border border-[#c3c6d1] rounded-lg text-xs bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none"
                        value={formLicenciaDesde}
                        onChange={(e) => setFormLicenciaDesde(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-[#737780] uppercase tracking-wider" htmlFor="input-lic-hasta">
                        Fecha Fin (Opcional)
                      </label>
                      <input
                        id="input-lic-hasta"
                        type="date"
                        className="w-full p-2 border border-[#c3c6d1] rounded-lg text-xs bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none"
                        value={formLicenciaHasta}
                        onChange={(e) => setFormLicenciaHasta(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Observaciones */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-[#737780] uppercase tracking-wider" htmlFor="input-lic-obs">
                      Observaciones / Diagnóstico
                    </label>
                    <textarea
                      id="input-lic-obs"
                      className="w-full p-3 border border-[#c3c6d1] rounded-lg text-xs bg-white focus:ring-1 focus:ring-[#001e40] focus:border-[#001e40] outline-none resize-none"
                      placeholder="Detalles sobre el reposo o justificación de licencia..."
                      rows={3}
                      value={formLicenciaMotivo}
                      onChange={(e) => setFormLicenciaMotivo(e.target.value)}
                    />
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col gap-2 pt-2">
                    <button
                      type="button"
                      onClick={handleCreateLicencia}
                      disabled={formLicenciaCargando || tiposLicencias.length === 0}
                      className="w-full h-11 bg-[#001e40] hover:bg-[#003366] text-white rounded-lg font-semibold text-xs flex items-center justify-center gap-2 shadow-sm transition-all active:scale-95 disabled:opacity-50"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {formLicenciaCargando ? (
                        <>
                          <RefreshCw size={16} strokeWidth={2} className="animate-spin-slow" />
                          Cargando...
                        </>
                      ) : (
                        <>
                          <Save size={16} strokeWidth={2} />
                          Registrar Licencia
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setMostrarFormLicencia(false)}
                      className="w-full h-11 bg-transparent hover:bg-[#eae8e7] text-[#001e40] border border-[#c3c6d1] rounded-lg font-semibold text-xs flex items-center justify-center transition-all"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modal Footer */}
        <div className="p-4 bg-[#f6f3f2] border-t border-[#c3c6d1] flex-shrink-0">
          <button
            className="w-full h-12 bg-transparent hover:bg-[#eae8e7] text-[#001e40] border border-[#c3c6d1] rounded-lg font-semibold text-sm transition-all tap-active"
            style={{ fontFamily: 'Inter, sans-serif' }}
            onClick={onClose}
          >
            Cerrar Ficha
          </button>
        </div>
      </m.div>
    </m.div>
  );
}

function GradoMasivoModal({
  open,
  onClose,
  compania,
  onConfirm,
  loading,
  gradoSelected,
  onGradoChange
}: {
  open: boolean;
  onClose: () => void;
  compania: string;
  onConfirm: () => void;
  loading: boolean;
  gradoSelected: string;
  onGradoChange: (grado: string) => void;
}) {
  if (!open) return null;

  const grados = [
    'Aspirante a Cadete',
    'Cadete',
    'Oficial',
    'Sargento',
    'Subteniente',
    'Teniente',
    'Teniente Primero',
    'Oficial Subayudante',
    'Oficial Ayudante',
    'Oficial Subinspector',
    'Oficial Inspector',
    'Oficial Principal'
  ];

  return (
    <m.div {...MODAL_OVERLAY} className="fixed inset-0 z-[9000] flex items-center justify-center p-4 bg-[#001e40]/40 backdrop-blur-sm" onClick={onClose}>
      <m.div
        {...MODAL_TARJETA}
        className="relative w-full max-w-md shadow-2xl rounded-xl overflow-hidden border border-[#c3c6d1] flex flex-col bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="bg-[#001e40] p-5 flex justify-between items-start flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-white">
              <Medal size={20} strokeWidth={1.8} />
            </div>
            <div>
              <h3 className="font-bold text-base text-white" style={{ fontFamily: 'Inter, sans-serif' }}>Jerarquía General</h3>
              <p className="text-xs text-white/70 uppercase tracking-widest mt-0.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                {compania}
              </p>
            </div>
          </div>
          <button className="text-white/70 hover:text-white hover:bg-white/10 p-1 rounded-full transition-colors tap-active" onClick={onClose} disabled={loading}>
            <X size={20} strokeWidth={2} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-4">
          <p className="text-xs text-[#43474f] leading-relaxed">
            Seleccione la jerarquía que desea aplicar a <strong>todos los cursantes activos</strong> de la compañía:
          </p>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-[#737780]" htmlFor="masivo-grado-select">
              Nueva Jerarquía
            </label>
            <select
              id="masivo-grado-select"
              className="w-full p-3 border border-[#c3c6d1] rounded-lg text-sm bg-white focus:ring-2 focus:ring-[#001e40] focus:border-[#001e40] outline-none transition-all"
              value={gradoSelected}
              onChange={(e) => onGradoChange(e.target.value)}
              disabled={loading}
            >
              {grados.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>

          <div className="p-3 rounded-lg bg-[#ffdad6] text-[#ba1a1a] border border-[#ffdad6]/50 flex items-start gap-2.5">
            <AlertTriangle size={18} strokeWidth={2} className="mt-0.5 flex-shrink-0" />
            <p className="text-[11px] leading-snug font-medium">
              Esta acción modificará la jerarquía de todos los cursantes de esta compañía en la base de datos de manera inmediata.
            </p>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-5 bg-[#f6f3f2] border-t border-[#c3c6d1] flex gap-3">
          <button
            className="flex-1 h-12 bg-transparent text-[#001e40] hover:bg-[#eae8e7] border border-[#c3c6d1] rounded-lg font-semibold text-sm transition-all"
            style={{ fontFamily: 'Inter, sans-serif' }}
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            className="flex-1 h-12 bg-[#001e40] hover:bg-[#003366] text-white rounded-lg font-semibold text-sm flex items-center justify-center gap-2 shadow-lg active:scale-95 transition-all disabled:opacity-50"
            style={{ fontFamily: 'Inter, sans-serif' }}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Procesando...' : 'Aplicar Grado'}
          </button>
        </div>
      </m.div>
    </m.div>
  );
}

export function CadetesList({ fecha, activeTab, compania, onRegistroChange, onCompaniaChange: _onCompaniaChange }: CadetesListProps) {
  const { pendingCount, isOnline, sincronizar } = useOfflineSync();
  const [cadetes, setCadetes] = useState<Cadete[]>([]);
  const [filtro, setFiltro] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstado>('todos');
  const [registrosEstado, setRegistrosEstado] = useState<Map<number, 'presente' | 'ausente' | 'tarde'>>(new Map());
  const [registrosMotivo, setRegistrosMotivo] = useState<Map<number, string>>(new Map());
  const [modalHistorial, setModalHistorial] = useState(false);
  const [cadeteHistorial, setCadeteHistorial] = useState<Cadete | null>(null);
  const [historialRegistros, setHistorialRegistros] = useState<RegistroPresentismo[]>([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const [busquedaDashboard, setBusquedaDashboard] = useState('');
  const [loading, setLoading] = useState(true);

  // Estados para modal dinámico de asistencia (Apple-Style)
  const [showFiltroModal, setShowFiltroModal] = useState(false);
  const [filtroModalEstado, setFiltroModalEstado] = useState<'convocada' | 'efectiva' | 'dv' | 'aac' | 'exc' | 'mic' | 'presentes' | 'ausentes' | null>(null);
  const [modalSearchQuery, setModalSearchQuery] = useState('');


  // Nuevos estados para Presentismo: Orden de Llegada
  const [licenciasHoy, setLicenciasHoy] = useState<Map<number, string>>(new Map());
  const [marcarPresentesOpen, setMarcarPresentesOpen] = useState(false);
  const [registrosHora, setRegistrosHora] = useState<Map<number, string>>(new Map());
  const [criterioOrden, setCriterioOrden] = useState<'cronologico' | 'alfabetico'>('cronologico');

  // Nuevos estados para Gestión General y Jerarquías
  const [filtroGestion, setFiltroGestion] = useState<'activos' | 'licencias' | 'desistidos' | 'convocatoria' | 'excluidos' | 'traspaso'>('activos');
  const [modalGradoMasivo, setModalGradoMasivo] = useState(false);
  const [gradoMasivoSelected, setGradoMasivoSelected] = useState('Aspirante a Cadete');
  const [cargandoGradoMasivo, setCargandoGradoMasivo] = useState(false);

  const normalizeDni = (v: string) => v.replace(/\D/g, '');
  const normalizeText = (v: string) => v.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  useEffect(() => { loadDatos(); }, [fecha]);


  // El listener se registra una sola vez, pero siempre invoca la versión más
  // reciente de loadDatos (fecha/compañía actuales, no las del primer render).
  const loadDatosRef = useRef<(silent?: boolean) => Promise<void>>(async () => {});
  useEffect(() => {
    const silentReload = () => { void loadDatosRef.current(true); };
    window.addEventListener('sgcc:sync-data-changed', silentReload);
    return () => window.removeEventListener('sgcc:sync-data-changed', silentReload);
  }, []);

  const loadDatos = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const storedCadetes = await storage.getCadetes();
      
      // Filtrar por activos y por compañía seleccionada
      const filtered = storedCadetes.filter((c) => {
        const isActivo = c.activo;
        const matchesCia = !compania || c.compania === compania;
        return isActivo && matchesCia;
      });
      
      setCadetes(filtered);

      // Solo registros de cadetes de la compañía/lista actual (las stats no deben
      // contar marcas de otras compañías para la misma fecha)
      const idsCompania = new Set(filtered.map((c) => c.id));
      const registros = (await storage.getRegistrosByFecha(fecha)).filter((r) => idsCompania.has(r.cadete_id));
      const estadoMap = new Map<number, 'presente' | 'ausente' | 'tarde'>();
      const motivoMap = new Map<number, string>();
      const horaMap = new Map<number, string>();
      registros.forEach((r) => {
        estadoMap.set(r.cadete_id, r.estado);
        if (r.motivo) motivoMap.set(r.cadete_id, r.motivo);
        if (r.hora_local) horaMap.set(r.cadete_id, r.hora_local);
      });
      setRegistrosEstado(estadoMap);
      setRegistrosMotivo(motivoMap);
      setRegistrosHora(horaMap);

      // Cargar licencias locales y filtrar las activas hoy
      const allLicencias = await storage.getLicencias();
      const licMap = new Map<number, string>();
      allLicencias.forEach((lic) => {
        // fecha_fin null = licencia abierta (p. ej. hasta alta médica)
        if (lic.fecha_inicio <= fecha && (!lic.fecha_fin || lic.fecha_fin >= fecha)) {
          licMap.set(lic.cadete_id, lic.tipo_codigo || 'LIC');
        }
      });
      setLicenciasHoy(licMap);
    } catch (error) {
      console.error('Error al cargar cadetes:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };
  loadDatosRef.current = loadDatos;

  const obtenerCoordenadas = (): Promise<{ latitud: number; longitud: number } | null> =>
    new Promise((resolve) => {
      if (!navigator.geolocation) { resolve(null); return; }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitud: pos.coords.latitude, longitud: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 3500, maximumAge: 10000 }
      );
    });

  const saveEstado = async (cadeteId: number, estado: 'presente' | 'ausente' | 'tarde', motivo?: string) => {
    const now = new Date();
    const horaLocal = formatHoraLocal(now);
    let latitud: number | undefined;
    let longitud: number | undefined;

    try {
      const coords = await obtenerCoordenadas();
      if (coords) { latitud = coords.latitud; longitud = coords.longitud; }
    } catch {}

    await storage.saveRegistro(
      {
        cadete_id: cadeteId,
        fecha,
        hora_local: horaLocal,
        estado,
        motivo,
        latitud,
        longitud,
        dispositivo_info: {
          user_agent: navigator.userAgent,
          platform: navigator.platform,
          timestamp: now.toISOString(),
        },
      },
      true
    );

    setRegistrosEstado((prev) => new Map(prev).set(cadeteId, estado));
    setRegistrosHora((prev) => new Map(prev).set(cadeteId, horaLocal));
    setRegistrosMotivo((prev) => {
      const next = new Map(prev);
      if (motivo?.trim()) next.set(cadeteId, motivo.trim());
      else next.delete(cadeteId);
      return next;
    });

    onRegistroChange?.();

    if (navigator.onLine) {
      sincronizar({ silent: true, pull: false }).catch((err) => console.error('Error en sincronización automática tras saveEstado:', err));
    }
  };

  // Presentes en bloque (MarcarPresentesSheet). Mismo registro que saveEstado,
  // pero en una sola transacción de IndexedDB y una sola sincronización.
  const marcarPresentesEnBloque = async (ids: number[]) => {
    // Confirmar primero las cuentas regresivas abiertas (no pisarlas)
    solicitarFlushMarcas();
    await esperarMarcasEnVuelo(5000);
    const fin = iniciarMarcaEnVuelo();
    try {
      // Releer: excluir a quien ya tenga registro o licencia vigente
      const yaMarcados = new Set((await storage.getRegistrosByFecha(fecha)).map((r) => r.cadete_id));
      const aMarcar = ids.filter((id) => !yaMarcados.has(id) && !licenciasHoy.has(id));
      if (aMarcar.length === 0) {
        toast.info('No quedaban cadetes pendientes para marcar.');
        return;
      }
      const now = new Date();
      const horaLocal = formatHoraLocal(now);
      let coords: { latitud: number; longitud: number } | null = null;
      try { coords = await obtenerCoordenadas(); } catch { /* sin ubicación */ }
      const dispositivo_info = { user_agent: navigator.userAgent, platform: navigator.platform, timestamp: now.toISOString() };

      await storage.saveRegistros(aMarcar.map((cadete_id) => ({
        cadete_id,
        fecha,
        hora_local: horaLocal,
        estado: 'presente' as const,
        motivo: undefined,
        latitud: coords?.latitud,
        longitud: coords?.longitud,
        dispositivo_info,
      })), true);

      setRegistrosEstado((prev) => { const n = new Map(prev); aMarcar.forEach((id) => n.set(id, 'presente')); return n; });
      setRegistrosHora((prev) => { const n = new Map(prev); aMarcar.forEach((id) => n.set(id, horaLocal)); return n; });
      setRegistrosMotivo((prev) => { const n = new Map(prev); aMarcar.forEach((id) => n.delete(id)); return n; });
      onRegistroChange?.();

      const quedan = pendientesMarcables.length - aMarcar.length;
      toast.success(`${aMarcar.length} presente${aMarcar.length === 1 ? '' : 's'} marcado${aMarcar.length === 1 ? '' : 's'}`
        + (quedan > 0 ? ` · ${quedan} queda${quedan === 1 ? '' : 'n'} pendiente${quedan === 1 ? '' : 's'}` : ''));
    } catch (err) {
      console.error('Error al marcar presentes en bloque:', err);
      toast.error('No se pudieron guardar los presentes. Intente de nuevo.');
      throw err;
    } finally {
      fin();
    }
    if (navigator.onLine) {
      sincronizar({ silent: true, pull: false }).catch((err) => console.error('Error en sincronización tras marcar presentes:', err));
    }
  };

  const handleEstadoChange = async (cadeteId: number, estado: 'presente' | 'ausente' | 'tarde', motivo?: string) => {
    await saveEstado(cadeteId, estado, motivo);
  };

  const handleDesmarcar = async (cadeteId: number) => {
    // Tombstone pendiente: la desmarcación también se sincroniza al servidor
    await storage.desmarcarRegistro(cadeteId, fecha);
    setRegistrosEstado((prev) => { const n = new Map(prev); n.delete(cadeteId); return n; });
    setRegistrosHora((prev) => { const n = new Map(prev); n.delete(cadeteId); return n; });
    setRegistrosMotivo((prev) => { const n = new Map(prev); n.delete(cadeteId); return n; });
    onRegistroChange?.();

    if (navigator.onLine) {
      sincronizar({ silent: true, pull: false }).catch((err) => console.error('Error en sincronización automática tras handleDesmarcar:', err));
    }
  };

  const handleGradoMasivoConfirm = async () => {
    if (!compania) {
      toast.error('No se puede aplicar un cambio masivo sin una compañía seleccionada.');
      return;
    }
    try {
      setCargandoGradoMasivo(true);

      const allCadetes = await storage.getCadetes();
      const affected = allCadetes.filter(c => c.compania === compania && c.grado !== gradoMasivoSelected);
      for (const c of affected) {
        await storage.saveCadeteLocal({ ...c, grado: gradoMasivoSelected });
      }

      await storage.enqueueOp({ type: 'updateGradoCompania', payload: { compania, grado: gradoMasivoSelected } });
      window.dispatchEvent(new CustomEvent('sgcc:sync-data-changed'));
      if (navigator.onLine) {
        sincronizar().catch((err) => console.error('Error en sincronización automática tras handleGradoMasivoConfirm:', err));
      }
      await loadDatos(true);
      setModalGradoMasivo(false);
    } catch (err) {
      console.error('Error al actualizar grado masivamente:', err);
      toast.error(err instanceof Error ? err.message : 'Error al actualizar el grado.');
    } finally {
      setCargandoGradoMasivo(false);
    }
  };

  // Sorted cadetes (alphabetical by apellido, nombres, numero)
  const cadetesOrdenados = useMemo(() =>
    [...cadetes].sort((a, b) => {
      const byApellido = (a.apellido ?? '').localeCompare(b.apellido ?? '', 'es', { sensitivity: 'base' });
      if (byApellido !== 0) return byApellido;
      const byNombres = (a.nombres ?? '').localeCompare(b.nombres ?? '', 'es', { sensitivity: 'base' });
      return byNombres;
    }),
  [cadetes]);

  // Presentismo, dashboard y modal de fuerza usan el estado institucional que regía en
  // la fecha de la jornada: una baja posterior no cuenta, y un no efectivo (D.V., EXC.,
  // A.A.C., M.I.C.) no suma como presente/ausente aunque tenga una marca ese día.
  // Gestión muestra siempre el estado actual.
  const estadoEnFecha = (c: Cadete) => estadoInstitucionalEnFecha(c, fecha);
  const efectivoEnFecha = (c: Cadete) => esEfectivoEnFecha(c, fecha);

  // Filtered for Listado tab
  const cadetesFiltrados = useMemo(() => {
    let result = [...cadetes];

    if (filtro.trim()) {
      const fLower = normalizeText(filtro);
      const fDni = normalizeDni(filtro);
      const tokens = fLower.split(/\s+/).filter(Boolean);
      result = result.filter((c) => {
        const fullName = normalizeText(`${c.apellido ?? ""} ${c.nombres ?? ""}`);
        const matchTokens = tokens.length > 0 && tokens.every((t) => fullName.includes(t));
        const dniStr = String(c.dni || '');
        return matchTokens || dniStr.includes(filtro) || (fDni.length > 0 && normalizeDni(dniStr).includes(fDni));
      });

      if (activeTab === 'gestion') {
        if (filtroGestion === 'activos') {
          result = result.filter((c) => !ESTADOS_QUE_ALTERAN_FUERZA.includes(c.estado_institucional || ''));
        } else if (filtroGestion === 'licencias') {
          // Licencia vigente en la fecha seleccionada
          result = result.filter((c) => licenciasHoy.has(c.id));
        } else if (filtroGestion === 'desistidos') {
          result = result.filter((c) => c.estado_institucional === 'Desestimiento');
        } else if (filtroGestion === 'convocatoria') {
          result = result.filter((c) => c.estado_institucional === 'Ausente a Convocatoria');
        } else if (filtroGestion === 'excluidos') {
          result = result.filter((c) => c.estado_institucional === 'Excluido');
        } else if (filtroGestion === 'traspaso') {
          result = result.filter((c) => c.estado_institucional === 'Traspaso');
        }
      } else {
        // En presentismo, si hay búsqueda, buscamos sobre toda la FUERZA CONVOCADA (todos excepto Traspaso)
        result = result.filter((c) => estadoEnFecha(c) !== 'Traspaso');
      }
    } else {
      if (activeTab === 'gestion') {
        if (filtroGestion === 'activos') {
          result = result.filter((c) => !ESTADOS_QUE_ALTERAN_FUERZA.includes(c.estado_institucional || ''));
        } else if (filtroGestion === 'licencias') {
          // Licencia vigente en la fecha seleccionada
          result = result.filter((c) => licenciasHoy.has(c.id));
        } else if (filtroGestion === 'desistidos') {
          result = result.filter((c) => c.estado_institucional === 'Desestimiento');
        } else if (filtroGestion === 'convocatoria') {
          result = result.filter((c) => c.estado_institucional === 'Ausente a Convocatoria');
        } else if (filtroGestion === 'excluidos') {
          result = result.filter((c) => c.estado_institucional === 'Excluido');
        } else if (filtroGestion === 'traspaso') {
          result = result.filter((c) => c.estado_institucional === 'Traspaso');
        }
      } else {
        if (filtroEstado === 'convocada') {
          result = result.filter((c) => estadoEnFecha(c) !== 'Traspaso');
        } else if (filtroEstado === 'todos' || filtroEstado === 'pendientes') {
          result = result.filter((c) => !registrosEstado.has(c.id) && efectivoEnFecha(c));
        } else if (filtroEstado === 'presentes') {
          result = result.filter((c) => registrosEstado.get(c.id) === 'presente' && efectivoEnFecha(c));
        } else if (filtroEstado === 'ausentes') {
          result = result.filter((c) => registrosEstado.get(c.id) === 'ausente' && efectivoEnFecha(c));
        }
      }
    }

    // Ordenamiento por defecto alfabético
    result.sort((a, b) => {
      const byApellido = (a.apellido ?? '').localeCompare(b.apellido ?? '', 'es', { sensitivity: 'base' });
      if (byApellido !== 0) return byApellido;
      return (a.nombres ?? '').localeCompare(b.nombres ?? '', 'es', { sensitivity: 'base' });
    });

    // Pendientes: los que tienen licencia vigente van al final (no se marcan presentes)
    if (activeTab === 'presentismo' && (filtroEstado === 'todos' || filtroEstado === 'pendientes')) {
      result = [...result.filter((c) => !licenciasHoy.has(c.id)), ...result.filter((c) => licenciasHoy.has(c.id))];
    }

    // En caso de presentes y criterio cronológico, se aplica orden de llegada
    if (activeTab === 'presentismo' && filtroEstado === 'presentes' && criterioOrden === 'cronologico') {
      result.sort((a, b) => {
        const horaA = registrosHora.get(a.id) || '23:59:59';
        const horaB = registrosHora.get(b.id) || '23:59:59';
        return horaA.localeCompare(horaB);
      });
    }

    return result;
  }, [cadetes, fecha, filtro, filtroEstado, registrosEstado, registrosHora, criterioOrden, activeTab, filtroGestion, licenciasHoy]);

  // Dashboard search results
  const resultadosBusqueda = useMemo(() => {
    const termino = normalizeText(busquedaDashboard.trim());
    if (!termino) return [];
    const fDni = normalizeDni(busquedaDashboard);
    const tokens = termino.split(/\s+/).filter(Boolean);
    return cadetesOrdenados
      .filter((c) => {
        const fullName = normalizeText(`${c.apellido ?? ""} ${c.nombres ?? ""}`);
        const matchTokens = tokens.length > 0 && tokens.every((t) => fullName.includes(t));
        const dniStr = String(c.dni || '');
        return matchTokens || dniStr.includes(busquedaDashboard.trim()) || (fDni.length > 0 && normalizeDni(dniStr).includes(fDni));
      })
      .slice(0, 6);
  }, [busquedaDashboard, cadetesOrdenados]);

  // Memo para cadetes filtrados del modal de asistencia (Apple-Style)
  const modalCadetesFiltrados = useMemo(() => {
    if (!filtroModalEstado) return [];
    
    let base: Cadete[] = [];
    switch (filtroModalEstado) {
      case 'convocada':
        base = cadetes.filter((c) => estadoEnFecha(c) !== 'Traspaso');
        break;
      case 'efectiva':
        base = cadetes.filter(efectivoEnFecha);
        break;
      case 'dv':
        base = cadetes.filter((c) => estadoEnFecha(c) === 'Desestimiento');
        break;
      case 'aac':
        base = cadetes.filter((c) => estadoEnFecha(c) === 'Ausente a Convocatoria');
        break;
      case 'exc':
        base = cadetes.filter((c) => estadoEnFecha(c) === 'Excluido');
        break;
      case 'mic':
        base = cadetes.filter((c) => estadoEnFecha(c) === 'Traspaso');
        break;
      case 'presentes':
        base = cadetes.filter((c) => registrosEstado.get(c.id) === 'presente' && efectivoEnFecha(c));
        break;
      case 'ausentes':
        base = cadetes.filter((c) => registrosEstado.get(c.id) === 'ausente' && efectivoEnFecha(c));
        break;
      default:
        base = [];
    }

    const query = normalizeText(modalSearchQuery.trim());
    if (!query) return base;

    const queryDni = normalizeDni(modalSearchQuery);
    const tokens = query.split(/\s+/).filter(Boolean);

    return base.filter((c) => {
      const fullName = normalizeText(`${c.apellido ?? ""} ${c.nombres ?? ""}`);
      const matchTokens = tokens.length > 0 && tokens.every((t) => fullName.includes(t));
      const dniStr = String(c.dni || '');
      return matchTokens || dniStr.includes(modalSearchQuery.trim()) || (queryDni.length > 0 && normalizeDni(dniStr).includes(queryDni));
    });
  }, [filtroModalEstado, cadetes, fecha, registrosEstado, modalSearchQuery]);

  // Resultados de búsqueda para Orden de Llegada

  // Llegadas de hoy ordenadas por hora descendente (el más reciente arriba)

  // Pendientes reales: efectivos en la fecha, sin registro y sin licencia vigente.
  // Los de licencia no están "pendientes": ya figuran por su licencia en el Parte.
  const ordenAlfabetico = (a: Cadete, b: Cadete) =>
    (a.apellido ?? '').localeCompare(b.apellido ?? '', 'es', { sensitivity: 'base' })
    || (a.nombres ?? '').localeCompare(b.nombres ?? '', 'es', { sensitivity: 'base' });
  const pendientesMarcables = useMemo(
    () => cadetes.filter((c) => !registrosEstado.has(c.id) && efectivoEnFecha(c) && !licenciasHoy.has(c.id)).sort(ordenAlfabetico),
    [cadetes, fecha, registrosEstado, licenciasHoy]
  );
  const conLicenciaSinMarca = useMemo(
    () => cadetes
      .filter((c) => !registrosEstado.has(c.id) && efectivoEnFecha(c) && licenciasHoy.has(c.id))
      .sort(ordenAlfabetico)
      .map((c) => ({ cadete: c, codigo: licenciasHoy.get(c.id) || 'LIC' })),
    [cadetes, fecha, registrosEstado, licenciasHoy]
  );

  // Stats — Fuerza Convocada / Efectiva y asistencia del día.
  // Presentes/ausentes/tardes cuentan sólo la fuerza efectiva (igual que el Parte de Novedades).
  const stats = useMemo(() => {
    const contar = (estado: string) => cadetes.filter((c) => estadoEnFecha(c) === estado).length;
    const traspasos = contar('Traspaso');
    const convocada = cadetes.length - traspasos;
    const desestimientos = contar('Desestimiento');
    const exclusiones = contar('Excluido');
    const ausentesConvocatoria = contar('Ausente a Convocatoria');
    const efectiva = convocada - desestimientos - exclusiones - ausentesConvocatoria;
    const marcasEfectivas = cadetes
      .filter((c) => registrosEstado.has(c.id) && efectivoEnFecha(c))
      .map((c) => registrosEstado.get(c.id));
    const procesados = marcasEfectivas.length;
    const presentes = marcasEfectivas.filter((e) => e === 'presente').length;
    const ausentes = marcasEfectivas.filter((e) => e === 'ausente').length;
    const tardes = marcasEfectivas.filter((e) => e === 'tarde').length;
    const pendientes = pendientesMarcables.length;
    const total = convocada;
    const pct = convocada > 0 ? Math.round((procesados / convocada) * 100) : 0;
    return { convocada, efectiva, desestimientos, exclusiones, ausentesConvocatoria, traspasos, total, procesados, presentes, ausentes, tardes, pendientes, pct };
  }, [cadetes, fecha, registrosEstado, pendientesMarcables]);



  // Abre historial
  const abrirHistorial = async (cadete: Cadete) => {
    setCadeteHistorial(cadete);
    setModalHistorial(true);
    setCargandoHistorial(true);
    try {
      const todos = await storage.getRegistros();
      const hist = todos
        .filter((r) => r.cadete_id === cadete.id)
        .sort((a, b) => {
          const da = `${a.fecha}T${a.hora_local || '00:00'}`;
          const db = `${b.fecha}T${b.hora_local || '00:00'}`;
          return db.localeCompare(da);
        });
      setHistorialRegistros(hist);
    } catch {
      setHistorialRegistros([]);
    } finally {
      setCargandoHistorial(false);
    }
  };





  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 gap-3">
        <div className="skeleton w-14 h-14 rounded-full" />
        <div className="skeleton w-40 h-4 rounded" />
        <p className="text-sm" style={{ color: 'var(--color-sgcc-outline)' }}>Cargando cadetes...</p>
      </div>
    );
  }

  // ──────────────────────────────────────────
  // TAB: DASHBOARD
  // ──────────────────────────────────────────
  if (activeTab === 'dashboard') {
    return (
      <div className="p-4 space-y-4 animate-fade-in select-none">
        
        {/* HEADER / STATUS DE FUERZA (Apple HIG) */}
        <div className="flex items-center justify-between px-1.5 mb-1.5">
          <div className="flex flex-col text-left">
            <h2 className="text-base font-extrabold tracking-tight text-neutral-800 font-sans">
              Resumen de Fuerza
            </h2>
            <p className="text-[10px] font-extrabold text-neutral-400 uppercase tracking-widest mt-0.5">
              Estado General del Personal
            </p>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/70 backdrop-blur-md border border-[#3c3c43]/8 shadow-sm">
            <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-[#34C759]' : 'bg-[#FF9500]'} animate-pulse`} />
            <span className="text-[9.5px] font-bold text-neutral-600 uppercase tracking-tight font-sans">
              {isOnline ? 'Conectado' : 'Sin Conexión'}
            </span>
            {pendingCount > 0 && (
              <span className="ml-1 text-[8.5px] font-black px-1.5 py-0.2 rounded bg-gradient-to-r from-[#FF9500] to-[#FFCC00] text-white shadow-sm font-mono">
                {pendingCount}
              </span>
            )}
          </div>
        </div>

        {/* BUSCADOR DE CADETE EN PANEL (iOS Spotlight search style) */}
        <div
          className="rounded-[24px] border overflow-hidden bg-white/70 backdrop-blur-md shadow-[0_2px_12px_rgba(0,0,0,0.015)]"
          style={{ borderColor: 'var(--color-sgcc-outline-variant)' }}
        >
          <label
            htmlFor="dashboard-search-input"
            className="px-4.5 py-3 border-b bg-neutral-50/50 flex items-center justify-between cursor-pointer select-none active:bg-neutral-100/50 transition-colors"
            style={{ borderColor: 'var(--color-sgcc-outline-variant)' }}
          >
            <span className="text-[9px] font-extrabold uppercase tracking-widest text-neutral-400">
              Buscar Cadete
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF] animate-ping" />
          </label>
          <div className="p-3.5">
            <div className="relative group">
              <Search size={15} strokeWidth={2.5} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 group-focus-within:text-[#007AFF] transition-colors duration-200" />
              <input
                id="dashboard-search-input"
                type="text"
                placeholder="Buscar por Apellido, Nombre o DNI..."
                value={busquedaDashboard}
                onChange={(e) => setBusquedaDashboard(e.target.value)}
                className="w-full h-10 pl-10 pr-8 rounded-xl border text-xs outline-none bg-neutral-100/80 border-transparent focus:border-[#007AFF]/30 focus:bg-white focus:ring-4 focus:ring-[#007AFF]/8 transition-all duration-300 placeholder:text-neutral-450 text-neutral-850 font-sans font-semibold text-left"
              />
              {busquedaDashboard && (
                <button
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-200/60 rounded-full transition-colors"
                  onClick={() => setBusquedaDashboard('')}
                >
                  <X size={14} strokeWidth={2.5} className="text-neutral-500 hover:text-neutral-800" />
                </button>
              )}
            </div>

            {busquedaDashboard.trim() && (
              <div
                className="mt-2 border rounded-2xl overflow-hidden bg-white/90 backdrop-blur-md shadow-lg divide-y divide-[#3c3c43]/8 max-h-48 overflow-y-auto"
                style={{ borderColor: 'var(--color-sgcc-outline-variant)' }}
              >
                {resultadosBusqueda.length === 0 ? (
                  <p className="p-3.5 text-xs italic text-center text-neutral-400">
                    Ningún cadete coincide
                  </p>
                ) : (
                  resultadosBusqueda.map((cadete) => (
                    <button
                      key={cadete.id}
                      className="w-full p-3.5 text-left transition-colors flex items-center justify-between hover:bg-neutral-50 active:bg-neutral-100/50 group"
                      onClick={() => abrirHistorial(cadete)}
                    >
                      <div className="flex flex-col text-left">
                        <p className="text-xs font-bold text-neutral-850 group-hover:text-[#007AFF] transition-colors">
                          {cadete.apellido}, {cadete.nombres}
                        </p>
                        <p className="text-[10px] text-neutral-400 mt-0.5">
                          DNI {cadete.dni} • {cadete.compania || 'Sin sección'}
                        </p>
                      </div>
                      <ChevronRight size={14} strokeWidth={2.2} className="text-neutral-300 group-hover:text-neutral-500 transition-colors font-sans font-semibold" />
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* GRID DE MÉTRICAS (Convocada y Efectiva) */}
        <div className="grid grid-cols-2 gap-3.5">
          
          {/* Tarjeta Convocada */}
          <div
            onClick={() => {
              setFiltroModalEstado('convocada');
              setShowFiltroModal(true);
            }}
            className="rounded-[24px] p-4.5 border flex flex-col justify-between h-[115px] cursor-pointer bg-gradient-to-br from-white/95 to-slate-50/70 border-[#007AFF]/12 hover:border-[#007AFF]/35 shadow-[0_4px_24px_rgba(0,0,0,0.02)] transition-all duration-300 ease-out active:scale-[0.96] hover:shadow-md group text-left"
          >
            <div className="flex justify-between items-start">
              <div className="w-8.5 h-8.5 rounded-xl bg-gradient-to-br from-[#007AFF]/10 to-[#0056B3]/3 flex items-center justify-center transition-colors group-hover:bg-[#007AFF]/15">
                <Users size={18} strokeWidth={2.2} style={{ color: '#007AFF' }} />
              </div>
              <span className="text-[8.5px] font-black px-2 py-0.5 rounded-full bg-slate-100 text-[#001e40] uppercase tracking-wider">
                Total
              </span>
            </div>
            <div className="flex flex-col text-left">
              <p className="text-3xl font-black tracking-tight text-neutral-800 leading-none">
                <AnimatedNumber value={stats.convocada} />
              </p>
              <p className="text-[9px] uppercase tracking-wider font-extrabold text-neutral-400 mt-1">
                Convocada
              </p>
            </div>
          </div>

          {/* Tarjeta Efectiva */}
          <div
            onClick={() => {
              setFiltroModalEstado('efectiva');
              setShowFiltroModal(true);
            }}
            className="rounded-[24px] p-4.5 border flex flex-col justify-between h-[115px] cursor-pointer bg-gradient-to-br from-[#f0f9f4]/95 to-[#e6f4ea]/60 border-[#34C759]/15 hover:border-[#34C759]/35 shadow-[0_4px_24px_rgba(0,0,0,0.02)] transition-all duration-300 ease-out active:scale-[0.96] hover:shadow-md group text-left"
          >
            <div className="flex justify-between items-start">
              <div className="w-8.5 h-8.5 rounded-xl bg-gradient-to-br from-[#34C759]/12 to-[#28A745]/3 flex items-center justify-center transition-colors group-hover:bg-[#34C759]/18">
                <BadgeCheck size={18} strokeWidth={2.2} style={{ color: '#34C759' }} />
              </div>
              <span className="text-[8.5px] font-black px-2 py-0.5 rounded-full bg-[#34C759]/10 text-[#34C759] uppercase tracking-wider">
                Apto
              </span>
            </div>
            <div className="flex flex-col text-left">
              <p className="text-3xl font-black tracking-tight text-[#1a6f3c] leading-none">
                <AnimatedNumber value={stats.efectiva} />
              </p>
              <p className="text-[9px] uppercase tracking-wider font-extrabold text-[#1a6f3c]/70 mt-1">
                Efectiva
              </p>
            </div>
          </div>

        </div>



        {/* DESGLOSE PANEL DE SUB-ESTADOS (Descontables en lista agrupada iOS) */}
        <div
          className="rounded-[24px] border overflow-hidden bg-white/70 backdrop-blur-md shadow-[0_2px_12px_rgba(0,0,0,0.012)]"
          style={{ borderColor: 'var(--color-sgcc-outline-variant)' }}
        >
          {/* Header */}
          <div className="px-4.5 py-3 border-b border-[#3c3c43]/8 flex items-center justify-between">
            <span className="text-[9px] font-extrabold uppercase tracking-widest text-neutral-400">
              Desglose de Novedades
            </span>
            <span className="text-[8.5px] font-black text-[#ba1a1a] px-2 py-0.5 rounded-full bg-[#ba1a1a]/6 tracking-wide uppercase font-sans">
              Descuentan Fuerza
            </span>
          </div>

          {/* iOS Grouped List Container */}
          <div className="divide-y divide-[#3c3c43]/8 font-sans">
            
            {/* D.V. */}
            <div
              onClick={() => {
                setFiltroModalEstado('dv');
                setShowFiltroModal(true);
              }}
              className="flex items-center justify-between px-4.5 py-3.5 hover:bg-neutral-50/60 active:bg-neutral-100/50 cursor-pointer transition-all duration-200 group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-[9px] bg-gradient-to-br from-[#FF9500] to-[#FFCC00] flex items-center justify-center text-white shadow-sm flex-shrink-0">
                  <AlertTriangle size={16} strokeWidth={2.2} />
                </div>
                <div className="flex flex-col text-left">
                  <span className="text-[13px] font-bold text-neutral-800 group-hover:text-[#001e40] transition-colors">Desistimiento Voluntario</span>
                  <span className="text-[9px] font-bold text-neutral-400 uppercase tracking-wide">D.V.</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-extrabold text-neutral-750 bg-neutral-100 px-2.5 py-1 rounded-lg">
                  <AnimatedNumber value={stats.desestimientos} />
                </span>
                <ChevronRight size={14} strokeWidth={2.2} className="text-neutral-300 group-hover:text-neutral-500 transition-colors" />
              </div>
            </div>

            {/* A.A.C. */}
            <div
              onClick={() => {
                setFiltroModalEstado('aac');
                setShowFiltroModal(true);
              }}
              className="flex items-center justify-between px-4.5 py-3.5 hover:bg-neutral-50/60 active:bg-neutral-100/50 cursor-pointer transition-all duration-200 group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-[9px] bg-gradient-to-br from-[#5856D6] to-[#AF52DE] flex items-center justify-center text-white shadow-sm flex-shrink-0">
                  <Info size={16} strokeWidth={2.2} />
                </div>
                <div className="flex flex-col text-left">
                  <span className="text-[13px] font-bold text-neutral-800 group-hover:text-[#001e40] transition-colors">Ausente a Convocatoria</span>
                  <span className="text-[9px] font-bold text-neutral-400 uppercase tracking-wide">A.A.C.</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-extrabold text-neutral-750 bg-neutral-100 px-2.5 py-1 rounded-lg">
                  <AnimatedNumber value={stats.ausentesConvocatoria} />
                </span>
                <ChevronRight size={14} strokeWidth={2.2} className="text-neutral-300 group-hover:text-neutral-500 transition-colors" />
              </div>
            </div>

            {/* EXC. */}
            <div
              onClick={() => {
                setFiltroModalEstado('exc');
                setShowFiltroModal(true);
              }}
              className="flex items-center justify-between px-4.5 py-3.5 hover:bg-neutral-50/60 active:bg-neutral-100/50 cursor-pointer transition-all duration-200 group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-[9px] bg-gradient-to-br from-[#FF3B30] to-[#FF453A] flex items-center justify-center text-white shadow-sm flex-shrink-0">
                  <UserX size={16} strokeWidth={2.2} />
                </div>
                <div className="flex flex-col text-left">
                  <span className="text-[13px] font-bold text-neutral-800 group-hover:text-[#001e40] transition-colors">Exclusión por Novedad</span>
                  <span className="text-[9px] font-bold text-neutral-400 uppercase tracking-wide">EXC.</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-extrabold text-neutral-750 bg-neutral-100 px-2.5 py-1 rounded-lg">
                  <AnimatedNumber value={stats.exclusiones} />
                </span>
                <ChevronRight size={14} strokeWidth={2.2} className="text-neutral-300 group-hover:text-neutral-500 transition-colors" />
              </div>
            </div>

            {/* M.I.C. */}
            <div
              onClick={() => {
                setFiltroModalEstado('mic');
                setShowFiltroModal(true);
              }}
              className="flex items-center justify-between px-4.5 py-3.5 hover:bg-neutral-50/60 active:bg-neutral-100/50 cursor-pointer transition-all duration-200 group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-[9px] bg-gradient-to-br from-[#007AFF] to-[#5AC8FA] flex items-center justify-center text-white shadow-sm flex-shrink-0">
                  <ArrowUpDown size={16} strokeWidth={2.2} />
                </div>
                <div className="flex flex-col text-left">
                  <span className="text-[13px] font-bold text-neutral-800 group-hover:text-[#001e40] transition-colors">Movimiento Interno Compañía</span>
                  <span className="text-[9px] font-bold text-neutral-400 uppercase tracking-wide">M.I.C.</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-extrabold text-neutral-750 bg-neutral-100 px-2.5 py-1 rounded-lg">
                  <AnimatedNumber value={stats.traspasos} />
                </span>
                <ChevronRight size={14} strokeWidth={2.2} className="text-neutral-300 group-hover:text-neutral-500 transition-colors" />
              </div>
            </div>

          </div>
        </div>

        {/* ASISTENCIA DIARIA (Presentes y Ausentes) */}
        <div className="grid grid-cols-2 gap-3.5">
          
          {/* Presentes */}
          <div
            onClick={() => {
              setFiltroModalEstado('presentes');
              setShowFiltroModal(true);
            }}
            className="rounded-[24px] p-4.5 border flex flex-col justify-between h-[115px] cursor-pointer bg-gradient-to-br from-white/95 to-slate-50/70 border-emerald-500/15 hover:border-emerald-500/35 shadow-[0_4px_24px_rgba(0,0,0,0.02)] transition-all duration-300 ease-out active:scale-[0.96] hover:shadow-md group text-left"
          >
            <div className="flex justify-between items-start">
              <div className="w-8.5 h-8.5 rounded-xl bg-gradient-to-br from-[#34C759]/10 to-[#28A745]/3 flex items-center justify-center transition-colors group-hover:bg-[#34C759]/15">
                <CheckCircle2 size={18} strokeWidth={2.2} style={{ color: '#34C759' }} />
              </div>
              <span className="text-[8.5px] font-black px-2 py-0.5 rounded-full bg-emerald-100/70 text-emerald-800 uppercase tracking-wider">
                Presente
              </span>
            </div>
            <div className="flex flex-col text-left">
              <p className="text-3xl font-black tracking-tight text-neutral-850 leading-none">
                <AnimatedNumber value={stats.presentes} />
              </p>
              <p className="text-[9px] uppercase tracking-wider font-extrabold text-emerald-700/80 mt-1">
                Presentes
              </p>
            </div>
          </div>

          {/* Ausentes */}
          <div
            onClick={() => {
              setFiltroModalEstado('ausentes');
              setShowFiltroModal(true);
            }}
            className="rounded-[24px] p-4.5 border flex flex-col justify-between h-[115px] cursor-pointer bg-gradient-to-br from-white/95 to-slate-50/70 border-rose-500/15 hover:border-rose-500/35 shadow-[0_4px_24px_rgba(0,0,0,0.02)] transition-all duration-300 ease-out active:scale-[0.96] hover:shadow-md group text-left"
          >
            <div className="flex justify-between items-start">
              <div className="w-8.5 h-8.5 rounded-xl bg-gradient-to-br from-[#FF3B30]/10 to-[#FF453A]/3 flex items-center justify-center transition-colors group-hover:bg-[#FF3B30]/15">
                <XCircle size={18} strokeWidth={2.2} style={{ color: '#FF3B30' }} />
              </div>
              <span className="text-[8.5px] font-black px-2 py-0.5 rounded-full bg-rose-100 text-rose-800 uppercase tracking-wider">
                Ausente
              </span>
            </div>
            <div className="flex flex-col text-left">
              <p className="text-3xl font-black tracking-tight text-neutral-850 leading-none">
                <AnimatedNumber value={stats.ausentes} />
              </p>
              <p className="text-[9px] uppercase tracking-wider font-extrabold text-rose-800/80 mt-1">
                Ausentes
              </p>
            </div>
          </div>

        </div>



        {/* Historial modal */}
        <AnimatePresence>
          {modalHistorial && cadeteHistorial && (
            <HistorialModal
              open
              cadete={cadeteHistorial}
              registros={historialRegistros}
              loading={cargandoHistorial}
              onClose={() => setModalHistorial(false)}
              sincronizar={sincronizar}
            />
          )}
        </AnimatePresence>

        {/* ====================================================
             MODAL DE ASISTENCIA DIARIA APPLE-STYLE (TSX)
             ==================================================== */}
        <AnimatePresence>
        {showFiltroModal && filtroModalEstado && (
          <m.div key="modal-asistencia" className="fixed inset-0 z-[9000] flex items-center justify-center p-4 md:p-6">
            
            {/* Backdrop blur background */}
            <m.div
              {...MODAL_OVERLAY}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-md cursor-pointer"
              onClick={() => {
                setShowFiltroModal(false);
                setModalSearchQuery('');
              }}
            />

            {/* Modal Box */}
            <m.div
              {...MODAL_TARJETA}
              className="w-full max-w-lg h-[550px] md:h-[600px] shadow-[0_32px_80px_rgba(0,0,0,0.22)] rounded-[2.2rem] flex flex-col overflow-hidden relative z-10 border font-sans"
              style={{
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(30px) saturate(190%)',
                WebkitBackdropFilter: 'blur(30px) saturate(190%)',
                borderColor: 'rgba(255, 255, 255, 0.45)',
              }}
            >
              {/* Apple Drag Line */}
              <div className="h-6 flex justify-center items-center bg-white/20 select-none flex-shrink-0">
                <div className="w-10 h-1 rounded-full bg-black/15" />
              </div>

              {/* Modal Header */}
              <div className="px-6 pb-4 pt-1 border-b border-[#3c3c43]/8 bg-white/30 flex items-start justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div
                    className="p-3 rounded-2xl flex items-center justify-center shadow-sm"
                    style={{
                      backgroundColor:
                        filtroModalEstado === 'convocada'
                          ? 'rgba(0, 122, 255, 0.08)'
                          : filtroModalEstado === 'efectiva'
                          ? 'rgba(52, 199, 89, 0.08)'
                          : filtroModalEstado === 'dv'
                          ? 'rgba(255, 149, 0, 0.08)'
                          : filtroModalEstado === 'aac'
                          ? 'rgba(88, 86, 214, 0.08)'
                          : filtroModalEstado === 'exc'
                          ? 'rgba(255, 59, 48, 0.08)'
                          : filtroModalEstado === 'mic'
                          ? 'rgba(90, 200, 250, 0.08)'
                          : filtroModalEstado === 'presentes'
                          ? 'rgba(52, 199, 89, 0.08)'
                          : 'rgba(255, 59, 48, 0.08)',
                      color:
                        filtroModalEstado === 'convocada'
                          ? '#007AFF'
                          : filtroModalEstado === 'efectiva'
                          ? '#34C759'
                          : filtroModalEstado === 'dv'
                          ? '#FF9500'
                          : filtroModalEstado === 'aac'
                          ? '#5856D6'
                          : filtroModalEstado === 'exc'
                          ? '#FF3B30'
                          : filtroModalEstado === 'mic'
                          ? '#007AFF'
                          : filtroModalEstado === 'presentes'
                          ? '#34C759'
                          : '#FF3B30',
                    }}
                  >
                    {filtroModalEstado === 'convocada' && <Users size={22} strokeWidth={2} />}
                    {filtroModalEstado === 'efectiva' && <BadgeCheck size={22} strokeWidth={2} />}
                    {filtroModalEstado === 'dv' && <AlertTriangle size={22} strokeWidth={2} />}
                    {filtroModalEstado === 'aac' && <Info size={22} strokeWidth={2} />}
                    {filtroModalEstado === 'exc' && <UserX size={22} strokeWidth={2} />}
                    {filtroModalEstado === 'mic' && <ArrowUpDown size={22} strokeWidth={2} />}
                    {filtroModalEstado === 'presentes' && <CheckCircle2 size={22} strokeWidth={2} />}
                    {filtroModalEstado === 'ausentes' && <XCircle size={22} strokeWidth={2} />}
                  </div>
                  <div className="flex flex-col text-left">
                    <h3 className="text-sm font-extrabold text-[#001e40] tracking-tight">
                      {filtroModalEstado === 'convocada' && 'Fuerza Convocada Total'}
                      {filtroModalEstado === 'efectiva' && 'Fuerza Efectiva Real'}
                      {filtroModalEstado === 'dv' && 'Desistimientos Voluntarios (D.V.)'}
                      {filtroModalEstado === 'aac' && 'Ausentes a Convocatoria (A.A.C.)'}
                      {filtroModalEstado === 'exc' && 'Exclusiones por Novedad (EXC.)'}
                      {filtroModalEstado === 'mic' && 'Movimientos Internos de Compañía (M.I.C.)'}
                      {filtroModalEstado === 'presentes' && 'Cadetes Presentes'}
                      {filtroModalEstado === 'ausentes' && 'Cadetes Ausentes'}
                    </h3>
                    <p className="text-[10px] uppercase font-bold tracking-widest text-neutral-400 mt-0.5">
                      {filtroModalEstado === 'convocada' && 'Plantel Orgánico Convocado a Servicio'}
                      {filtroModalEstado === 'efectiva' && 'Personal Convocado Excluyendo Novedades'}
                      {filtroModalEstado === 'dv' && 'Novedades de Renuncia o Baja Voluntaria'}
                      {filtroModalEstado === 'aac' && 'No Asistieron al Llamado Inicial'}
                      {filtroModalEstado === 'exc' && 'Excluidos por Junta o Disposición Médica'}
                      {filtroModalEstado === 'mic' && 'Personal Reasignado de Sección'}
                      {filtroModalEstado === 'presentes' && 'Asistencia Corroborada'}
                      {filtroModalEstado === 'ausentes' && 'Inasistencias del Día Registradas'}
                    </p>
                  </div>
                </div>

                <button
                  className="p-1.5 rounded-full bg-black/5 hover:bg-black/10 transition-colors"
                  onClick={() => {
                    setShowFiltroModal(false);
                    setModalSearchQuery('');
                  }}
                >
                  <X size={16} strokeWidth={2.2} className="text-neutral-500 hover:text-neutral-800" />
                </button>
              </div>

              {/* Modal Search Bar */}
              <div className="px-6 py-3 bg-white/10 border-b border-[#3c3c43]/8 flex gap-2 items-center flex-shrink-0">
                <div className="relative flex-1">
                  <Search size={14} strokeWidth={2.2} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input
                    type="text"
                    value={modalSearchQuery}
                    onChange={(e) => setModalSearchQuery(e.target.value)}
                    placeholder="Buscar cadete por nombre o DNI dentro de este subgrupo..."
                    className="w-full h-8.5 pl-9 pr-4 rounded-xl text-xs outline-none bg-white/50 border border-white/40 focus:border-[#007AFF]/30 focus:bg-white/80 transition-all placeholder:text-neutral-450 text-neutral-800 font-sans text-left font-semibold"
                  />
                </div>
                <span className="px-2.5 py-0.5 text-[10px] font-black rounded-lg bg-[#007AFF] text-white shadow-sm font-mono flex-shrink-0">
                  {modalCadetesFiltrados.length}
                </span>
              </div>

              {/* Cadet Scrollable List */}
              <div
                className="flex-1 overflow-y-auto px-6 py-4 space-y-2.5 bg-white/20 divide-y divide-[#3c3c43]/6"
                style={{ scrollbarWidth: 'thin' }}
              >
                {modalCadetesFiltrados.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-4 text-center select-none animate-fade-in">
                    <div className="p-4 rounded-3xl bg-neutral-100 text-neutral-400 mb-3 flex items-center justify-center">
                      <SearchX size={36} strokeWidth={1.5} />
                    </div>
                    <h4 className="text-sm font-extrabold text-neutral-700">Sin Resultados</h4>
                    <p className="text-xs text-neutral-400 mt-1 max-w-[240px]">
                      No hay ningún cadete en este estado o no coinciden con la búsqueda.
                    </p>
                  </div>
                ) : (
                  modalCadetesFiltrados.map((cadete, index) => {
                    const initials = `${(cadete.apellido ?? '').charAt(0)}${(cadete.nombres ?? '').charAt(0)}`.toUpperCase();
                    const avatarBg =
                      index % 2 === 0
                        ? 'bg-blue-100 text-blue-750 font-sans font-semibold'
                        : 'bg-indigo-100 text-indigo-755 font-sans font-semibold';

                    let badgeHtml = null;
                    const estadoInst = estadoEnFecha(cadete);
                    if (estadoInst && estadoInst !== 'Cadete' && estadoInst !== 'Aspirante a Cadete') {
                      let novColor = '';
                      let novBg = '';
                      if (estadoInst === 'Desestimiento') {
                        novColor = 'text-amber-800 border-amber-250';
                        novBg = 'bg-amber-100/60';
                      } else if (estadoInst === 'Ausente a Convocatoria') {
                        novColor = 'text-purple-800 border-purple-250';
                        novBg = 'bg-purple-100/60';
                      } else if (estadoInst === 'Excluido') {
                        novColor = 'text-rose-800 border-rose-250';
                        novBg = 'bg-rose-100/60';
                      } else if (estadoInst === 'Traspaso') {
                        novColor = 'text-sky-850 border-sky-250';
                        novBg = 'bg-sky-100/60';
                      }
                      badgeHtml = (
                        <span className={`px-2 py-0.5 rounded-full text-[8.5px] font-black uppercase border ${novColor} ${novBg}`}>
                          {estadoInst === 'Desestimiento' ? 'D.V.' : estadoInst === 'Ausente a Convocatoria' ? 'A.A.C.' : estadoInst === 'Excluido' ? 'EXC.' : 'M.I.C.'}
                        </span>
                      );
                    } else {
                      const estPres = registrosEstado.get(cadete.id);
                      if (estPres === 'presente') {
                        badgeHtml = (
                          <span className="px-2 py-0.5 rounded-full text-[8.5px] font-black uppercase border text-emerald-800 border-emerald-250 bg-emerald-100/60 font-sans">
                            Presente
                          </span>
                        );
                      } else if (estPres === 'ausente') {
                        badgeHtml = (
                          <span className="px-2 py-0.5 rounded-full text-[8.5px] font-black uppercase border text-rose-800 border-rose-250 bg-rose-100/60 font-sans">
                            Ausente
                          </span>
                        );
                      } else if (estPres === 'tarde') {
                        badgeHtml = (
                          <span className="px-2 py-0.5 rounded-full text-[8.5px] font-black uppercase border text-amber-800 border-amber-250 bg-amber-100/60 font-sans">
                            Tarde
                          </span>
                        );
                      } else {
                        badgeHtml = (
                          <span className="px-2 py-0.5 rounded-full text-[8.5px] font-black uppercase border text-neutral-600 border-neutral-350 bg-neutral-100/60 font-sans">
                            Pendiente
                          </span>
                        );
                      }
                    }

                    return (
                      <div
                        key={cadete.id}
                        onClick={() => {
                          setShowFiltroModal(false);
                          setModalSearchQuery('');
                          abrirHistorial(cadete);
                        }}
                        className="flex items-center justify-between p-3.5 bg-white/70 hover:bg-white rounded-2xl border border-white/40 hover:border-neutral-300/40 shadow-sm transition-all duration-200 group hover:-translate-y-0.5 cursor-pointer pt-3 pb-3 animate-fade-in"
                      >
                        <div className="flex items-center gap-3.5">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs shadow-sm flex-shrink-0 ${avatarBg}`}>
                            {initials}
                          </div>
                          <div className="flex flex-col text-left font-sans">
                            <span className="text-xs font-bold text-neutral-850 group-hover:text-[#007AFF] transition-colors">
                              {cadete.apellido}, {cadete.nombres}
                            </span>
                            <div className="flex items-center gap-1.5 mt-0.5 text-[9px] font-semibold text-neutral-450">
                              <span>DNI {cadete.dni}</span>
                              <span>•</span>
                              <span>{cadete.compania || 'Sin sección'}</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {badgeHtml}
                          <ChevronRight size={14} className="text-neutral-300 group-hover:text-neutral-500 transition-colors animate-fade-in" />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-[#3c3c43]/8 bg-white/45 flex justify-between items-center text-[10px] font-semibold text-neutral-400 flex-shrink-0">
                <span>SGCC Asistencia Diaria</span>
                <span className="text-neutral-500 font-bold">{fecha}</span>
              </div>
            </m.div>
          </m.div>
        )}
        </AnimatePresence>
      </div>
    );
  }

  // TAB: PRESENTISMO — Nómina de asistencia, solo lectura (P/A/Llamar)
  // ──────────────────────────────────────────
  if (activeTab === 'presentismo') {
    return (
      <div className="flex flex-col h-full animate-fade-in">
        {/* Search + Company selector + Stats + Filters — sticky */}
        <section
          className="sticky z-40 py-3 px-3 space-y-3 border-b"
          style={{ backgroundColor: '#fbf9f8', borderColor: '#c3c6d1', top: 0 }}
        >
          {/* Company label — solo lectura, se cambia desde el menú del TopAppBar */}
          <div
            className="w-full h-10 flex items-center gap-2 px-4 border rounded-[12px] text-sm font-semibold"
            style={{ borderColor: '#c3c6d1', backgroundColor: '#f6f3f2', color: '#001e40' }}
          >
            <Building2 size={18} strokeWidth={1.8} style={{ color: '#737780', flexShrink: 0 }} />
            <span>{compania || 'Sin compañía'}</span>
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={18} strokeWidth={1.8} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#8E8E93' }} />
            <input
              id="listado-search"
              type="text"
              placeholder="Buscar por apellido o DNI..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="w-full h-12 pl-12 pr-10 text-ios-subhead outline-none transition-all"
              style={{
                borderRadius: '12px',
                border: '1px solid rgba(60,60,67,0.15)',
                backgroundColor: 'rgba(60,60,67,0.08)',
                color: '#1b1c1c',
              }}
            />
            {filtro && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center tap-active"
                style={{ backgroundColor: '#8E8E93' }}
                onClick={() => setFiltro('')}
              >
                <X size={12} strokeWidth={2.5} style={{ color: 'white' }} />
              </button>
            )}
          </div>

          {/* Filter grid — F. Convocada y F. Efectiva arriba, los 3 estados abajo */}
          <div className="space-y-2">
            {/* Fila superior: Fuerza Convocada y Fuerza Efectiva */}
            <div className="grid grid-cols-2 gap-2">
              {/* Fuerza Convocada */}
              <button
                className="flex items-center gap-2 rounded-[14px] px-3 py-2.5 border transition-all active:scale-95"
                style={{
                  backgroundColor: filtroEstado === 'convocada' ? '#001e40' : '#f0f4ff',
                  borderColor: filtroEstado === 'convocada' ? '#001e40' : '#c3c6d1',
                }}
                onClick={() => setFiltroEstado('convocada')}
              >
                <Users size={20} strokeWidth={1.8} style={{ color: filtroEstado === 'convocada' ? '#ffffff' : '#001e40', flexShrink: 0 }} />
                <div className="text-left min-w-0">
                  <p className="text-base font-bold leading-none" style={{ color: filtroEstado === 'convocada' ? '#ffffff' : '#001e40' }}><AnimatedNumber value={stats.convocada} /></p>
                  <p className="text-[10px] font-semibold uppercase tracking-tight truncate" style={{ color: filtroEstado === 'convocada' ? 'rgba(255,255,255,0.7)' : '#43474f' }}>Fuerza Convocada</p>
                </div>
              </button>

              {/* Fuerza Efectiva → filtro todos */}
              <button
                className="flex items-center gap-2 rounded-[14px] px-3 py-2.5 border transition-all active:scale-95"
                style={{
                  backgroundColor: filtroEstado === 'todos' ? '#001e40' : '#f0f4ff',
                  borderColor: filtroEstado === 'todos' ? '#001e40' : '#c3c6d1',
                }}
                onClick={() => setFiltroEstado('todos')}
              >
                <BadgeCheck size={20} strokeWidth={1.8} style={{ color: filtroEstado === 'todos' ? '#ffffff' : '#001e40', flexShrink: 0 }} />
                <div className="text-left min-w-0">
                  <p className="text-base font-bold leading-none" style={{ color: filtroEstado === 'todos' ? '#ffffff' : '#001e40' }}><AnimatedNumber value={stats.efectiva} /></p>
                  <p className="text-[10px] font-semibold uppercase tracking-tight truncate" style={{ color: filtroEstado === 'todos' ? 'rgba(255,255,255,0.7)' : '#43474f' }}>Fuerza Efectiva</p>
                </div>
              </button>
            </div>

            {/* Fila inferior: Presentes, Ausentes, Pendientes */}
            <div className="grid grid-cols-3 gap-2">
              {/* Presentes */}
              <button
                className="flex items-center gap-1.5 rounded-[14px] px-2 py-2 border transition-all active:scale-95"
                style={{
                  backgroundColor: filtroEstado === 'presentes' ? '#1a6f3c' : 'rgba(16,185,129,0.08)',
                  borderColor: filtroEstado === 'presentes' ? '#1a6f3c' : '#c3c6d1',
                }}
                onClick={() => setFiltroEstado('presentes')}
              >
                <CheckCircle2 size={18} strokeWidth={1.8} style={{ color: filtroEstado === 'presentes' ? '#ffffff' : '#1a6f3c', flexShrink: 0 }} />
                <div className="text-left min-w-0">
                  <p className="text-sm font-bold leading-none" style={{ color: filtroEstado === 'presentes' ? '#ffffff' : '#1a6f3c' }}><AnimatedNumber value={stats.presentes} /></p>
                  <p className="text-[9px] font-semibold uppercase tracking-tight truncate mt-0.5" style={{ color: filtroEstado === 'presentes' ? 'rgba(255,255,255,0.7)' : '#1a6f3c' }}>Presentes</p>
                </div>
              </button>

              {/* Ausentes */}
              <button
                className="flex items-center gap-1.5 rounded-[14px] px-2 py-2 border transition-all active:scale-95"
                style={{
                  backgroundColor: filtroEstado === 'ausentes' ? '#ba1a1a' : 'rgba(186,26,26,0.06)',
                  borderColor: filtroEstado === 'ausentes' ? '#ba1a1a' : '#c3c6d1',
                }}
                onClick={() => setFiltroEstado('ausentes')}
              >
                <XCircle size={18} strokeWidth={1.8} style={{ color: filtroEstado === 'ausentes' ? '#ffffff' : '#ba1a1a', flexShrink: 0 }} />
                <div className="text-left min-w-0">
                  <p className="text-sm font-bold leading-none" style={{ color: filtroEstado === 'ausentes' ? '#ffffff' : '#ba1a1a' }}><AnimatedNumber value={stats.ausentes} /></p>
                  <p className="text-[9px] font-semibold uppercase tracking-tight truncate mt-0.5" style={{ color: filtroEstado === 'ausentes' ? 'rgba(255,255,255,0.7)' : '#ba1a1a' }}>Ausentes</p>
                </div>
              </button>

              {/* Pendientes */}
              <button
                className="flex items-center gap-1.5 rounded-[14px] px-2 py-2 border transition-all active:scale-95"
                style={{
                  backgroundColor: filtroEstado === 'pendientes' ? '#735c00' : 'rgba(115,92,0,0.06)',
                  borderColor: filtroEstado === 'pendientes' ? '#735c00' : '#c3c6d1',
                }}
                onClick={() => setFiltroEstado('pendientes')}
              >
                <Hourglass size={18} strokeWidth={1.8} style={{ color: filtroEstado === 'pendientes' ? '#ffffff' : '#735c00', flexShrink: 0 }} />
                <div className="text-left min-w-0">
                  <p className="text-sm font-bold leading-none" style={{ color: filtroEstado === 'pendientes' ? '#ffffff' : '#735c00' }}><AnimatedNumber value={stats.pendientes} /></p>
                  <p className="text-[9px] font-semibold uppercase tracking-tight truncate mt-0.5" style={{ color: filtroEstado === 'pendientes' ? 'rgba(255,255,255,0.7)' : '#735c00' }}>Pendientes</p>
                </div>
              </button>
            </div>
          </div>
        </section>

        {/* Selector de ordenamiento para presentes */}
        {filtroEstado === 'presentes' && cadetesFiltrados.length > 0 && (
          <div className="px-3 py-2 flex items-center justify-between border-b border-[#c3c6d1]/40 bg-[#fbf9f8] mx-3 my-2 rounded-xl border shadow-sm flex-shrink-0">
            <span className="text-[11px] font-bold text-[#43474f] flex items-center gap-1.5 uppercase tracking-wider">
              <ArrowUpDown size={14} strokeWidth={2} style={{ color: '#001e40' }} />
              Ordenar:
            </span>
            <div className="flex gap-1.5">
              <button
                onClick={() => setCriterioOrden('cronologico')}
                className="px-2.5 py-1.5 text-[10px] font-black rounded-[10px] border transition-all active:scale-95 flex items-center gap-1 cursor-pointer"
                style={{
                  backgroundColor: criterioOrden === 'cronologico' ? '#001e40' : '#ffffff',
                  borderColor: criterioOrden === 'cronologico' ? '#001e40' : '#c3c6d1',
                  color: criterioOrden === 'cronologico' ? '#ffffff' : '#43474f',
                }}
              >
                <Clock size={13} strokeWidth={2} />
                Por llegada
              </button>
              <button
                onClick={() => setCriterioOrden('alfabetico')}
                className="px-2.5 py-1.5 text-[10px] font-black rounded-[10px] border transition-all active:scale-95 flex items-center gap-1 cursor-pointer"
                style={{
                  backgroundColor: criterioOrden === 'alfabetico' ? '#001e40' : '#ffffff',
                  borderColor: criterioOrden === 'alfabetico' ? '#001e40' : '#c3c6d1',
                  color: criterioOrden === 'alfabetico' ? '#ffffff' : '#43474f',
                }}
              >
                <ArrowDownAZ size={13} strokeWidth={2} />
                Alfabético
              </button>
            </div>
          </div>
        )}

        {/* Marcar presentes en bloque: los pendientes quedan presentes salvo los destildados */}
        <AnimatePresence initial={false}>
          {pendientesMarcables.length > 0 && !filtro.trim() && (filtroEstado === 'todos' || filtroEstado === 'pendientes') && (
            <m.div
              key="marcar-presentes"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              style={{ overflow: 'hidden' }}
              className="px-3"
            >
              <m.button
                id="abrir-marcar-presentes"
                type="button"
                whileTap={{ scale: 0.97 }}
                onClick={() => setMarcarPresentesOpen(true)}
                className="mt-3 w-full min-h-[52px] rounded-[14px] px-4 flex items-center gap-3 text-left text-white"
                style={{ backgroundColor: '#1a6f3c', boxShadow: '0 4px 14px rgba(26,111,60,0.28)' }}
              >
                <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>
                  <CheckCheck size={20} strokeWidth={2.2} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[15px] font-bold leading-tight">Marcar presentes</span>
                  <span className="block text-[12px] opacity-90">
                    <AnimatedNumber value={pendientesMarcables.length} /> pendiente{pendientesMarcables.length === 1 ? '' : 's'} · elegí quién queda pendiente
                  </span>
                </span>
                <ChevronRight size={20} strokeWidth={2} className="opacity-80" />
              </m.button>
            </m.div>
          )}
        </AnimatePresence>

        <MarcarPresentesSheet
          open={marcarPresentesOpen}
          onClose={() => setMarcarPresentesOpen(false)}
          fecha={fecha}
          compania={compania}
          pendientes={pendientesMarcables}
          conLicencia={conLicenciaSinMarca}
          onConfirmar={marcarPresentesEnBloque}
        />

        {/* List — padded cards */}
        <div className="flex-1 overflow-y-auto px-3 pt-3">
          {cadetesFiltrados.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <SearchX size={40} strokeWidth={1.4} className="mb-3" style={{ color: '#c3c6d1' }} />
              <p className="text-sm font-medium" style={{ color: '#43474f' }}>Sin resultados</p>
              <p className="text-xs mt-1" style={{ color: '#737780' }}>Intenta con otro término de búsqueda</p>
            </div>
          ) : filtroEstado === 'ausentes' ? (
            (() => {
              const groups: { id: string; title: string; color: string; bg: string; Icon: LucideIcon; filter: (mot: string) => boolean }[] = [
                { id: 'aviso', title: 'Ausente con Aviso', color: '#1e40af', bg: '#dbeafe', Icon: BellRing, filter: (mot) => mot.startsWith('Ausente con aviso') },
                { id: 'sin_aviso', title: 'Ausente sin Aviso', color: '#ba1a1a', bg: '#ffdad6', Icon: BellOff, filter: (mot) => mot.startsWith('Ausente sin aviso') },
                { id: 'rmh', title: 'Licencia por Enfermedad - R.M.H', color: '#0f766e', bg: '#ccfbf1', Icon: Stethoscope, filter: (mot) => mot.startsWith('Licencia por Enfermedad - R.M.H') },
                { id: 'art', title: 'A.R.T.', color: '#b45309', bg: '#fef3c7', Icon: Activity, filter: (mot) => mot.startsWith('A.R.T.') },
                { id: 'especial', title: 'Licencia Especial', color: '#6b21a8', bg: '#f3e8ff', Icon: CalendarDays, filter: (mot) => mot.startsWith('Licencia Especial') },
                { id: 'permiso', title: 'Permiso Especial', color: '#0369a1', bg: '#e0f2fe', Icon: ClipboardCheck, filter: (mot) => mot.startsWith('Permiso Especial') },
                { id: 'otros', title: 'Otros Motivos', color: '#43474f', bg: '#eae8e7', Icon: Hourglass, filter: (mot) => !mot.startsWith('Ausente con aviso') && !mot.startsWith('Ausente sin aviso') && !mot.startsWith('Licencia por Enfermedad - R.M.H') && !mot.startsWith('A.R.T.') && !mot.startsWith('Licencia Especial') && !mot.startsWith('Permiso Especial') },
              ];

              return (
                <div className="space-y-6">
                  {groups.map((group) => {
                    const cadetesEnGrupo = cadetesFiltrados.filter((cadete) => {
                      const motivo = registrosMotivo.get(cadete.id) || '';
                      return group.filter(motivo);
                    });

                    if (cadetesEnGrupo.length === 0) return null;

                    return (
                      <div key={group.id} className="space-y-2">
                        {/* Cabecera del Grupo */}
                        <div
                          className="flex items-center justify-between px-3 py-2 rounded-xl border shadow-sm"
                          style={{
                            backgroundColor: group.bg,
                            borderColor: `${group.color}30`,
                            color: group.color,
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <group.Icon size={18} strokeWidth={1.8} style={{ flexShrink: 0 }} />
                            <span className="text-xs font-black tracking-wider uppercase">{group.title}</span>
                          </div>
                          <span
                            className="text-[10px] font-black px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: `${group.color}20`,
                              color: group.color,
                            }}
                          >
                            {cadetesEnGrupo.length} {cadetesEnGrupo.length === 1 ? 'cadete' : 'cadetes'}
                          </span>
                        </div>

                        {/* Listado de Cadetes del Grupo */}
                        <div className="space-y-2">
                          {cadetesEnGrupo.map((cadete) => (
                            <CadeteCard
                              key={cadete.id}
                              cadete={cadete}
                              estado={registrosEstado.get(cadete.id) || null}
                              motivo={registrosMotivo.get(cadete.id)}
                              hora={registrosHora.get(cadete.id)}
                              onEstadoChange={(estado, motivo) => handleEstadoChange(cadete.id, estado, motivo)}
                              onDesmarcar={() => handleDesmarcar(cadete.id)}
                              isListadoMode={true}
                              licenciaActiva={licenciasHoy.get(cadete.id)}
                              mode="presentismo"
                              sincronizar={sincronizar}
                              fecha={fecha}
                              onVerHistorial={() => abrirHistorial(cadete)}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            cadetesFiltrados.map((cadete) => (
              <CadeteCard
                key={cadete.id}
                cadete={cadete}
                estado={registrosEstado.get(cadete.id) || null}
                motivo={registrosMotivo.get(cadete.id)}
                hora={registrosHora.get(cadete.id)}
                onEstadoChange={(estado, motivo) => handleEstadoChange(cadete.id, estado, motivo)}
                onDesmarcar={() => handleDesmarcar(cadete.id)}
                isListadoMode={true}
                licenciaActiva={licenciasHoy.get(cadete.id)}
                mode="presentismo"
                sincronizar={sincronizar}
                fecha={fecha}
                onVerHistorial={() => abrirHistorial(cadete)}
              />
            ))
          )}
          <div className="h-4" />
        </div>

        {/* Historial modal */}
        <AnimatePresence>
          {modalHistorial && cadeteHistorial && (
            <HistorialModal
              open
              cadete={cadeteHistorial}
              registros={historialRegistros}
              loading={cargandoHistorial}
              onClose={() => setModalHistorial(false)}
              sincronizar={sincronizar}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  // TAB: GESTIÓN — Administración de cadetes (ficha editable, sin asistencia)
  // ──────────────────────────────────────────
  if (activeTab === 'gestion') {
    return (
      <div className="flex flex-col h-full animate-fade-in">
        {/* Search + Company selector — sticky */}
        <section
          className="sticky z-40 py-3 px-3 space-y-3 border-b"
          style={{ backgroundColor: '#fbf9f8', borderColor: '#c3c6d1', top: 0 }}
        >
          {/* Company label — solo lectura */}
          <div
            className="w-full h-10 flex items-center gap-2 px-4 border rounded-[12px] text-sm font-semibold"
            style={{ borderColor: '#c3c6d1', backgroundColor: '#f6f3f2', color: '#001e40' }}
          >
            <Building2 size={18} strokeWidth={1.8} style={{ color: '#737780', flexShrink: 0 }} />
            <span>{compania || 'Sin compañía'}</span>
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={18} strokeWidth={1.8} className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#8E8E93' }} />
            <input
              id="gestion-search"
              type="text"
              placeholder="Buscar por apellido o DNI..."
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              className="w-full h-12 pl-12 pr-10 text-ios-subhead outline-none transition-all"
              style={{
                borderRadius: '12px',
                border: '1px solid rgba(60,60,67,0.15)',
                backgroundColor: 'rgba(60,60,67,0.08)',
                color: '#1b1c1c',
              }}
            />
            {filtro && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center tap-active"
                style={{ backgroundColor: '#8E8E93' }}
                onClick={() => setFiltro('')}
              >
                <X size={12} strokeWidth={2.5} style={{ color: 'white' }} />
              </button>
            )}
          </div>

          {/* Botones de aplicación general y filtros rápidos */}
          {/* Botones de aplicación general y filtros rápidos — Autoadaptable y sin scroll en Mobile */}
          <div className="flex flex-wrap items-center gap-2 pb-1 select-none">
            {/* Grado Masivo */}
            <button
              onClick={() => setModalGradoMasivo(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black transition-all active:scale-95 flex-shrink-0 shadow-sm border border-[#001e40]/20 bg-[#001e40] text-white hover:bg-[#003366] cursor-pointer"
              style={{ fontFamily: 'Inter, sans-serif' }}
              title="Cambiar jerarquía a toda la compañía"
            >
              <Medal size={14} strokeWidth={2} />
              [Grado]
            </button>

            {/* Divisor vertical */}
            <div className="h-5 w-[1px] bg-[#c3c6d1] flex-shrink-0 mx-1" />

            {/* Activos (Filtro por defecto) */}
            <button
              onClick={() => setFiltroGestion('activos')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black transition-all active:scale-95 flex-shrink-0 shadow-sm border cursor-pointer"
              style={{
                fontFamily: 'Inter, sans-serif',
                backgroundColor: filtroGestion === 'activos' ? '#f0f4ff' : '#ffffff',
                borderColor: filtroGestion === 'activos' ? '#001e40' : '#c3c6d1',
                color: filtroGestion === 'activos' ? '#001e40' : '#43474f',
              }}
            >
              <Users size={14} strokeWidth={2} />
              Activos
            </button>

            {/* Licencias (vigentes en la fecha seleccionada) */}
            <button
              onClick={() => setFiltroGestion(filtroGestion === 'licencias' ? 'activos' : 'licencias')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black transition-all active:scale-95 flex-shrink-0 shadow-sm border cursor-pointer"
              style={{
                fontFamily: 'Inter, sans-serif',
                backgroundColor: filtroGestion === 'licencias' ? '#ccfbf1' : '#ffffff',
                borderColor: filtroGestion === 'licencias' ? '#0f766e' : '#c3c6d1',
                color: filtroGestion === 'licencias' ? '#0f766e' : '#43474f',
              }}
            >
              <Stethoscope size={14} strokeWidth={2} />
              <span className="hidden sm:inline">Licencias</span>
              <span className="inline sm:hidden">LIC.</span>
            </button>

            {/* Desistimientos */}
            <button
              onClick={() => setFiltroGestion(filtroGestion === 'desistidos' ? 'activos' : 'desistidos')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black transition-all active:scale-95 flex-shrink-0 shadow-sm border cursor-pointer"
              style={{
                fontFamily: 'Inter, sans-serif',
                backgroundColor: filtroGestion === 'desistidos' ? '#fef3c7' : '#ffffff',
                borderColor: filtroGestion === 'desistidos' ? '#d97706' : '#c3c6d1',
                color: filtroGestion === 'desistidos' ? '#b45309' : '#43474f',
              }}
            >
              <Undo2 size={14} strokeWidth={2} />
              <span className="hidden sm:inline">Desistimientos</span>
              <span className="inline sm:hidden">D.V.</span>
            </button>

            {/* Ausentes a Convocatoria */}
            <button
              onClick={() => setFiltroGestion(filtroGestion === 'convocatoria' ? 'activos' : 'convocatoria')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black transition-all active:scale-95 flex-shrink-0 shadow-sm border cursor-pointer"
              style={{
                fontFamily: 'Inter, sans-serif',
                backgroundColor: filtroGestion === 'convocatoria' ? '#f3e8ff' : '#ffffff',
                borderColor: filtroGestion === 'convocatoria' ? '#7c3aed' : '#c3c6d1',
                color: filtroGestion === 'convocatoria' ? '#6b21a8' : '#43474f',
              }}
            >
              <UserX size={14} strokeWidth={2} />
              <span className="hidden sm:inline">Ausentes Convocatoria</span>
              <span className="inline sm:hidden">A.A.C.</span>
            </button>

            {/* Exclusiones */}
            <button
              onClick={() => setFiltroGestion(filtroGestion === 'excluidos' ? 'activos' : 'excluidos')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black transition-all active:scale-95 flex-shrink-0 shadow-sm border cursor-pointer"
              style={{
                fontFamily: 'Inter, sans-serif',
                backgroundColor: filtroGestion === 'excluidos' ? '#ffdad6' : '#ffffff',
                borderColor: filtroGestion === 'excluidos' ? '#ba1a1a' : '#c3c6d1',
                color: filtroGestion === 'excluidos' ? '#ba1a1a' : '#43474f',
              }}
            >
              <XCircle size={14} strokeWidth={2} />
              <span className="hidden sm:inline">Exclusiones</span>
              <span className="inline sm:hidden">EXC.</span>
            </button>

            {/* Movimiento Interno (Traspaso) */}
            <button
              onClick={() => setFiltroGestion(filtroGestion === 'traspaso' ? 'activos' : 'traspaso')}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-black transition-all active:scale-95 flex-shrink-0 shadow-sm border cursor-pointer"
              style={{
                fontFamily: 'Inter, sans-serif',
                backgroundColor: filtroGestion === 'traspaso' ? '#e0f2fe' : '#ffffff',
                borderColor: filtroGestion === 'traspaso' ? '#0284c7' : '#c3c6d1',
                color: filtroGestion === 'traspaso' ? '#0369a1' : '#43474f',
              }}
            >
              <ArrowRightLeft size={14} strokeWidth={2} />
              <span className="hidden sm:inline">Mov. Interno</span>
              <span className="inline sm:hidden">M.I.C.</span>
            </button>
          </div>
        </section>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-3 pt-3">
          {cadetesFiltrados.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <SearchX size={40} strokeWidth={1.4} className="mb-3" style={{ color: '#c3c6d1' }} />
              <p className="text-sm font-medium" style={{ color: '#43474f' }}>Sin resultados</p>
              <p className="text-xs mt-1" style={{ color: '#737780' }}>Intenta con otro término de búsqueda</p>
            </div>
          ) : (
            cadetesFiltrados.map((cadete) => (
              <CadeteCard
                key={cadete.id}
                cadete={cadete}
                estado={null}
                onEstadoChange={() => {}}
                isListadoMode={true}
                licenciaActiva={licenciasHoy.get(cadete.id)}
                mode="gestion"
                sincronizar={sincronizar}
                onVerHistorial={() => abrirHistorial(cadete)}
              />
            ))
          )}
          <div className="h-4" />
        </div>

        {/* Historial modal */}
        <AnimatePresence>
          {modalHistorial && cadeteHistorial && (
            <HistorialModal
              open
              cadete={cadeteHistorial}
              registros={historialRegistros}
              loading={cargandoHistorial}
              onClose={() => setModalHistorial(false)}
              sincronizar={sincronizar}
            />
          )}
        </AnimatePresence>

        {/* Grado Masivo modal */}
        <AnimatePresence>
          {modalGradoMasivo && (
        <GradoMasivoModal
          open
          onClose={() => setModalGradoMasivo(false)}
          compania={compania || 'Sin compañía'}
          gradoSelected={gradoMasivoSelected}
          onGradoChange={setGradoMasivoSelected}
          onConfirm={handleGradoMasivoConfirm}
          loading={cargandoGradoMasivo}
        />
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ──────────────────────────────────────────
  // TAB: REPORTES
  // ──────────────────────────────────────────
  // Reportes tab is handled directly in App.tsx as a separate component
  return null;
}
